/**
 * 语文听写「整块粘贴」导入解析器（**纯函数，不碰数据库、不碰网络**）。
 *
 * 家长会把课本内容整理成一大段文本粘进来。这个模块负责把它变成
 * **逐行的结构化结果 + 逐行的错误报告**，供两级接口共用：
 *
 *   `POST /students/:id/dictation/import/preview` → 只解析 + 查库比对，不写
 *   `POST /students/:id/dictation/import/commit`  → 解析 + 写库（有效行照常导入）
 *
 * ## 支持的格式
 *
 * 分隔符：**制表符**（从 Excel/课本配套表格粘出来最常见）、**逗号**（中英文都认）、
 * **2 个以上空格**、**竖线 `|`**、**顿号 `、`**。同一份文本可以混用，
 * 每行独立判断。
 *
 * 列的顺序（**没有表头时**按这个顺序，多余的列忽略）：
 *
 *     课文 | 字 | 词 | 拼音 | 类别 | 课本 | 年级 | 学期 | 词语表
 *
 * 有表头时按表头名字对齐，**顺序随便、列可以少**。能认的表头名：
 *
 *     lesson / 课文 / 课题 / 课    → 课文（课题；也接受 `5 秋天`、`第5课 秋天`）
 *     no / 课序 / 序号             → 课序（可选，用于排序）
 *     unit / 单元                  → 单元号（可选）
 *     char / 字 / 生字 / 汉字      → 汉字
 *     word / 词 / 词语 / 组词      → 词语
 *     pinyin / 拼音                → 拼音（覆盖值；留空 = 自动生成）
 *     category / 类别 / 类型       → 类别（1类字/2类字/常用词/词语/成语）
 *     textbook / 课本 / 教材       → 课本（出版社 / 书名）
 *     edition / 版次 / 版本        → **版次**（**已不用**：认得这一列，读进来直接忽略）
 *     grade / 年级                 → 年级（"三年级" / "primary_3" 都认）
 *     semester / 学期              → 学期（"上"/"上册"/"上学期"/"下"/"不分"）
 *     fromTextbook / 词语表 / 是否词语表 → 是否来自课本词语表
 *     listType / 表 / 字表 / 收录表 → 这一行收录在哪张表（识字表/写字表/词语表）
 *
 * ## 版次（`edition`）**已经彻底不用了**（2026-10-18）；年级 / 学期才是身份
 *
 * 2026-10-16 曾经要求"版次必填且唯一"。用户录一年级下册时发现同一出版社的
 * 各册版次都不一样 —— 那条规则会让"同一本书的上下册"没法归到一起。最终口径：
 *
 *   · **一本书 = 一个具体的册**（出版社 + 年级 + 学期）；
 *   · 「版次」**不再要求、不再读进数据、也不报错** —— 旧文本里还留着这一列时，
 *     解析器**认得它、忽略它**（用户手里那批文本不能因为这一列突然报错）；
 *   · 年级 / 学期缺失时由服务层给逐行错误（课本按册建，猜不出来）。
 *
 * 示例（制表符分隔，带表头）：
 *
 *     课本	版次	年级	学期	字
 *     人民教育出版社	2024年8月第1版	一年级	上册	天
 *     人民教育出版社	2024年8月第1版	一年级	上册	地
 *
 * 旧写法（无表头的列序）示例：
 *
 *     课文	字	词	拼音	类别
 *     第1课 秋天	秋			qiū	1类字
 *     第1课 秋天	气			qì	1类字
 *     第1课 秋天		秋天	qiū tiān	词语
 *
 * ## 按「书 + 表」录入：整段课本附录的网格（2026-10-15 新增）
 *
 * 用户的实际录入姿势是**照着一本书后面的附录表整块粘**（而不是一课一课录）：
 *
 *     识字表
 *     天	地	人	你	我	他	一	二	三
 *     四	五	上	下	口	耳	目	手	足
 *     写字表
 *     一	二	三	上	口	目	耳	手
 *     词语表
 *     你好	我们	天地	上下
 *
 * ## ⚠️ 三张表各自"能录什么"：**逐个词元（token）**判定（2026-10-16 用户决定）
 *
 * 用户原话：
 *
 * > "如果是**识字表和写字表**，录这两个表的时候，**只能录单个的汉字**。"
 * > "如果是**词语表**，那就相反，**只能录词语或者是成语**。"
 * > "不是说每行只能一个汉字或者一个词……**每个汉字可以空格分开，可以录好多汉字**。"
 *
 * 也就是说，判定单位是**每个词元**，不是每一行：
 *
 * | 表类型 | 每个词元必须 | 一行可以有 |
 * | --- | --- | --- |
 * | 识字表 / 写字表（`shizi` / `xiezi`） | **恰好一个汉字** | 任意多个词元（空格 / Tab / 换行分隔都行） |
 * | 词语表（`cibiao`） | **词语或成语 = 2 个及以上汉字** | 同上 |
 *
 * 合法的录法（每张表一行多个、或一行一个，都认）：
 *
 *     识字表
 *     天 地 人 你 我 他
 *     口 耳 目 手 足
 *
 *     词语表
 *     天地 人口 耳目 手足
 *     日月 水田
 *
 * 非法的词元**逐条报错**（带行号 + **是哪个词元** + 人话原因），
 * **那一个词元不产生数据**；同一行里**合法的词元照常导入**
 * （理由见本文件「逐行报错」那一节：家长一次粘几百行，一个词元写错不该让
 *  整行白录 —— 而且录进来的字/词在库里都是**去重**的，重粘一遍不会产生重复行）。
 *
 * ⚠️ 这条规则**只在"录的是哪张表"已知时**才生效（表名行 / 表名上下文 / 表名列 /
 *    调用方的 `defaultListType`）。**通用整块导入**（"整块导入（高级）"）里
 *    `字` 列与 `词` 列是**两列**，一行写"字 + 由它组成的词"是那条入口的老用法，
 *    它**不受这条规则影响**（那是列，不是同一张表里的两个词元）。
 *    原因：把通用入口也收紧会让**老粘贴文本静默少录数据** —— 那是本项目最忌讳的事。
 *
 * ## ⚠️ 连着写（没有分隔符）的汉字：两张表**刻意相反**（2026-10-16 用户实测）
 *
 * 家长照课本后面的附录粘进来时，汉字常常**一个分隔符都没有**：
 *
 *     识字表
 *     天地人你我他
 *     一二三四五上下
 *
 *   · **识字表 / 写字表** → **自动按单个汉字拆开**（`autoSplitHanziRun`）：
 *     这一行就是 6 个生字，意图清清楚楚。拆出来的每个字**照常过准入规则**
 *     （"每个词元恰好一个汉字"一点没放宽），并且**界面上看得见**这件事
 *     （预览的"注意"里有一条"有 N 行没有分隔符，已按单个汉字自动拆开"，
 *      见 `describeAutoSplitHanziRun` —— 不许静默）；
 *   · **词语表** → **不拆、也不猜**（`ambiguousWordRun`）：`天地人口` 是一个词
 *     还是两个词，解析器**判断不出来** ⇒ 报一条人话错误（说清"无法判断词从哪里
 *     断开"、词语表必须用空格或 Tab 分开、并给出"想录生字请切到识字表"的建议）；
 *   · 判据是"**整行都是汉字**"：只要掺了标点 / 字母 / 数字，**一个字都不拆**，
 *     仍按既有规则逐词元报错。
 *
 * ## 分隔符：空格 / Tab / 换行都能用（同一行里也可以混用）
 *
 * 用户原话："录制或者录词都可以用**空格或者是制表符（就是tab键）**分隔，
 * 也可能会出现**换行符**"。
 *
 * 因为**一行里的每个词元都是同一类东西**（要么全是单字、要么全是词），
 * "用空格分隔"与"用空格表示字 + 词"这两件事**不再冲突** —— 于是消歧被简化成：
 *
 *   · 一行里出现 **Tab** → 按 Tab 分列（Excel 粘贴的形状）；
 *   · 没有 Tab 时，**空格（含全角空格）连续出现也算一个分隔符**，
 *     所以一行里 Tab 与空格**混用**也认（`天<TAB>地 人` → 三个词元）；
 *   · 连续空格、行首/行尾空白、全角空格一律折叠掉，不产生空词元；
 *   · 换行符就是"换一行"，每行独立判断。
 *
 * ⚠️ 但**列式写法**（`课文 | 字 | 词 | 拼音 | 类别 | …`）仍然要靠 **Tab**：
 *    只有 Tab（或逗号 / 竖线）才能表达"这是第几列"。用空格分列的列式粘贴
 *    没法与"一行多个词元"区分，所以文档里明确要求列式请用 Tab 或逗号。
 *
 * 这一整段的识别规则（**只认"表名行"**，所以不会和别的形状打架）：
 *   · 某一行**第一个单元格**能归一成表类型（识字表/写字表/词语表），
 *     这一行剩下的单元格里的**单字**（或表格形状下的**词**）逐个展开成数据行；
 *   · 或者整行**只有一个单元格**、且它就是一个表名 —— 它变成"下面的行属于哪张表"
 *     的上下文（与课文表头行同一个思路）；
 *   · 表名行本身也可以带内容（`识字表	天	地	人`），此时不产生上下文，只展开。
 *
 * ⚠️ 表类型**只会被表名行 / 上下文行 / 表头列 / 调用方默认值**设定，
 *    **永远不会从 `类别` 列的字面里猜** —— 否则一行 `天` 的 OCR 噪声就能让
 *    整份数据落到错误的表上（那种错在界面上完全看不出来）。
 *
 * ## 「课文行」当作上下文
 *
 * 当一行**只有课文、没有字也没有词**时，它被当作**课文表头行**：
 * 只用来设定"下面的行属于哪一课"，本身不产生数据（见 `contextOnly`）。
 * 于是家长可以直接按课本目录粘贴：
 *
 *     第1课 秋天
 *     秋	气	了	子	人	大
 *     秋天	天气	叶子	大人
 *     第2课 小小的船
 *     船	月	儿	头	里	坐
 *
 * ## 逐行报错（单行错误不影响整批）
 *
 * 已与用户确认的语义是 **「逐行报告 + 有效行照常导入」**：家长粘的是手工整理的
 * 数据，一行格式不对（比如某一课名忘了写、某个拼音多打了一个空格）不应该让
 * 另外 200 行一起被回滚 —— 那样家长只能靠反复试错才能把数据录进去。
 * 错误与警告都带 `line`（**原文行号，从 1 开始**，跳过空行也保持原行号）
 * 与 `raw`（原始整行），家长照着就能改。
 */

import { isPinyinSyllable, validateWordPinyin } from './pinyin.js';
import { normalizePinyinSyllable, normalizeWordPinyin } from './pinyin-syllable.js';
import { splitReadingTokens, type ReadingToken } from './import-reading-tokens.js';
import {
  LIST_TYPE_SHORT_LABELS,
  availableReadings,
  isHanziListType,
  isReadingOfChar,
  toListType,
  type HanziListType,
  type ListType,
} from './listing.js';
import { normalizeTextbookText } from './textbook-identity.js';

// ---------------------------------------------------------------- 类型

/** 一行里出现的字段（都做了 trim 与全角转半角归一）。 */
export interface ParsedImportRow {
  /** 原文行号（1 开始，空行也计数）。 */
  line: number;
  /** 原始整行（错误提示里回显用）。 */
  raw: string;
  /**
   * 是否是"只用来设定上下文"的课文表头行（本身不产生数据）。
   * 导入时这类行不计入"新增/已存在"，但会被用来解析课本与课文。
   */
  contextOnly: boolean;

  /** 课文课题（contextOnly 行必填）。 */
  lessonTitle?: string;
  /** 课序原始写法（"5" / "识字1" / "语文园地三" / "5*"）。 */
  lessonLabel?: string;
  /** 课序（数字部分，解析不出时为 0）。 */
  lessonNo?: number;
  /** 单元号。 */
  unitNo?: number;

  /** 单个汉字（1 类字 / 2 类字）。 */
  charText?: string;
  /** 词语（常用词 / 词语 / 成语）。 */
  wordText?: string;
  /** 拼音覆盖值（可为空 = 用自动生成值）。 */
  pinyin?: string;
  /**
   * 文件里这个位置的拼音**末尾标了 `?` / `？`**（2026-10-20 新增，用户定稿）：
   * 生成文件的助手**不确定这个读音**，要让用户在预览里点一下确认。
   *
   * ⚠️ `?` **不是拼音的一部分**：解析这一层已经把它剥掉（见
   *    `import-reading-tokens.ts` 的 `parsePair()`），`pinyin` 里永远没有 `?`。
   * ⚠️ 只有**字表**（识字表 / 写字表）会把它带下去：词语表没有"每个字一个位置"的
   *    确认入口，`?` 在那里只当格式标记剥掉（见 `buildReadingTokenRows`）。
   * ⚠️ 省略 / `false` = 这个音是确定的（老文件照旧）。
   */
  pinyinUncertain?: boolean;
  /**
   * 用户在**预览里确认过**这个位置的读音（本轮新增）：
   * 「确认这个音」或「换一个音」都会把那一处标成已确认。
   *
   * 它只影响"待确认"那一项校验：`pinyinUncertain && pinyinConfirmed` ⇒
   * 不再是待确认（预览里从黄色变绿色）。
   */
  pinyinConfirmed?: boolean;
  /**
   * **文件里这个位置写的拼音**（归一后的那一份，例如 `shuāng`）。
   *
   * 只在"文件真的写了拼音"时才有值 —— 用途只有一个：
   *   把它和**字典拼音**（`availableReadings(char)[0]`）比一比，
   *   不一样就把这个位置标成「待确认」，并在文案里说清"文件写的是什么"。
   *
   * ⚠️ 它**不是**落库用的读音：单音字落库一律用字典那个音（见
   *    `fillAutoReadingForChar()`）；多音字仍然用文件写的那个（既有行为）。
   */
  filePinyin?: string;
  /**
   * **文件里写的拼音 ≠ 字典拼音**（2026-10-21「B 方案」）。
   *
   * true ⇒ 这个位置要标成「待确认」（浅橙），用户对着课本确认之后才能保存。
   * 与文件里的 `?` 是**两件事，可以同时成立**（见 `pinyinMarkedUncertain`）。
   */
  pinyinMismatch?: boolean;
  /**
   * 文件里在这个位置的拼音末尾**标了 `?`**（生成文件的人不确定）。
   *
   * 与 `pinyinMismatch` 分开记：两者都要变成「待确认」，但**文案不一样**
   * （`?` 是"生成文件的人没把握"，不一致是"文件写的和字典不一样"）。
   */
  pinyinMarkedUncertain?: boolean;
  /** 原始"类别"文本（归一后由调用方映射成枚举）。 */
  category?: string;
  /** 课本版本（出版社代号或展示名，原样透传给服务层解析）。 */
  textbook?: string;
  /**
   * **版次**（`2024年8月第1版`）—— **已废弃（2026-10-18）**，服务层**不读它**。
   *
   * 旧文本里还可能有这一列；解析器认得、归一后放在这里，但：
   *   · 它**不参与**"哪一本书"的判定（身份 = 出版社 + 年级 + 学期）；
   *   · 缺它**不报错**；它也不写进数据库。
   * ⛔ 新代码不要读这个字段。
   */
  edition?: string;
  /** 年级 code（已归一成 primary_3 这种；无法识别时保留原文，由调用方报错）。 */
  gradeCode?: string;
  /** 学期（归一成 上 / 下 / 不分）。 */
  semester?: string;
  /** 是否来自课本词语表。 */
  fromTextbook?: boolean;
  /**
   * 这一行收录在哪张表（`shizi` / `xiezi` / `cibiao`）。
   *
   * ⚠️ 字行走 `hanzi_listing`（一个字可以**同时**有 `shizi` 与 `xiezi` 两行）；
   *    词行走 `word.from_textbook`（`cibiao` ⇒ true）。
   *    来源优先级：本行的表名列 > 表头里的表类型列 > 表名上下文行 > 调用方默认值。
   */
  listType?: ListType;
  /**
   * 词语按**字位置**给出的读音覆盖（`长	cháng` 这种"这个词里这个字读这个音"）。
   *
   * 形态与 `charText` 无关（它只说"第 i 个字读什么"），因为解析器这一层不查字表；
   * 服务层会拿它去和"整词拼音推出来的读音"对齐（见 `readingsFromWordPinyin`）。
   */
  wordReadings?: string[];
}

/** 逐行问题（error = 这一行不会导入；warning = 会导入但需要家长知道）。 */
export interface ImportIssue {
  /** 原文行号。 */
  line: number;
  raw: string;
  message: string;
  /**
   * 出问题的那个**词元**（token），例如 `天 天地` 里的 `天地`。
   *
   * ⚠️ 三张表的准入规则是**逐个词元**判的，所以"第 4 行有问题"对一个粘了
   *    50 个字元的家长来说等于没给信息 —— 必须说清是**哪一个**。
   *    整行级的问题（缺版次、课文行是空的……）不带这个字段。
   */
  token?: string;
}

/** `parseDictationImport` 的返回值。 */
export interface ParseImportResult {
  rows: ParsedImportRow[];
  errors: ImportIssue[];
  warnings: ImportIssue[];
  /** 识别到的表头（没有表头时为空数组）。 */
  header: string[];
  /** 识别到的分隔符（"tab" / "comma" / "spaces" / "pipe" / "ideographic"）。 */
  delimiter: DelimiterName;
  /** 文件里出现的课本版本（去重、保持出现顺序）。 */
  textbooks: string[];
  /** 解析出的实际数据行数（不含 contextOnly 行与出错行）。 */
  dataRowCount: number;
  /** 统计，便于前端直接展示。 */
  stats: {
    totalLines: number;
    blankLines: number;
    contextLines: number;
    /** 只含汉字数据的行。 */
    charRows: number;
    /** 只含词语数据的行。 */
    wordRows: number;
    /** 同时含字与词的行（"这个字 + 由它组成的词"）。 */
    bothRows: number;
    /**
     * 用户在框里粘了多少个**词元**（空格 / Tab / 换行切出来的 token 数）。
     * 这是"我录了 100 个字"里那个 100 —— 预览的账靠它核对。
     *
     * ⚠️ 它在整趟解析**结束时**由 `recognized + problem` 求出来 ——
     *    那两个数是在**每一条处理路径上**直接登记的，所以恒等式
     *    `tokens = recognized + problem` 是**结构上成立**的，而不是靠两处口径碰巧一致。
     */
    tokens: number;
    /**
     * 这些词元里**收下了**多少个（正常等于 `dataRowCount`）。
     *
     * ⚠️ 恒等式（预览里那张账靠它，见 `summarizeImportCounts`）：
     *    `tokens = recognized + problem`
     *    每个词元要么收下、要么报错，**不能两边都不算**。
     */
    recognized: number;
    /**
     * **没收下**的词元数：字表里写了词、词语表里写了单个字。
     *
     * ⚠️ 与 `errors.length` 不同：一条错误文案可能覆盖一个词元，
     *    而一行的两个字/词列都违规时是**两个**词元没收下。
     */
    problem: number;
  };
}

export type DelimiterName = 'tab' | 'comma' | 'spaces' | 'pipe' | 'ideographic';

// ---------------------------------------------------------------- 常量

/** 没有表头时的列顺序（多余的列忽略）。 */
const DEFAULT_COLUMNS = [
  'lesson',
  'char',
  'word',
  'pinyin',
  'category',
  'textbook',
  /**
   * ⚠️ 「版次」**有默认列位**（第 7 列）。
   *
   * 它是**必填**字段，所以"无表头粘贴"时也必须有一个位置能放它 —— 否则家长
   * 想用无表头的列式写法就永远填不进版次（只能改用带表头的写法）。
   * 它排在「课本」后面、「年级」前面：与带表头的自然写法一致。
   */
  'edition',
  'grade',
  'semester',
  'fromTextbook',
  'listType',
] as const;

/**
 * 所有可以出现在**表头**里的字段。
 *
 * 比 `DEFAULT_COLUMNS` 多两个（`no` / `unit`）：它们在"无表头"的默认列序里
 * 没有位置（否则课序会把"字"挤到第 2 列，与最常用的贴法冲突），
 * 但家长完全可以自己写一个带「课序」「单元」列的表头。
 */
const HEADER_FIELDS = [...DEFAULT_COLUMNS, 'no', 'unit'] as const;

type FieldKey = (typeof HEADER_FIELDS)[number];

/**
 * 表头别名表。**大小写与空格不敏感**（会先归一）。
 * 一个表头名只能映射到一个字段；没认出来的列被忽略（不报错 —— 家长多贴一列
 * 备注不该导致整份数据被拒）。
 */
const HEADER_ALIASES: Record<string, FieldKey> = {
  // 课文
  lesson: 'lesson',
  课文: 'lesson',
  课题: 'lesson',
  课: 'lesson',
  文章: 'lesson',
  // 课序 / 单元
  no: 'no',
  课序: 'no',
  序号: 'no',
  unit: 'unit',
  单元: 'unit',
  单元号: 'unit',
  // 字 / 词
  char: 'char',
  hanzi: 'char',
  字: 'char',
  生字: 'char',
  汉字: 'char',
  会写字: 'char',
  会认字: 'char',
  word: 'word',
  词: 'word',
  词语: 'word',
  组词: 'word',
  单词: 'word',
  // 拼音 / 类别
  pinyin: 'pinyin',
  拼音: 'pinyin',
  读音: 'pinyin',
  category: 'category',
  类别: 'category',
  类型: 'category',
  分类: 'category',
  // 课本 / 版次 / 年级 / 学期
  textbook: 'textbook',
  book: 'textbook',
  课本: 'textbook',
  教材: 'textbook',
  课本版本: 'textbook',
  /**
   * ⚠️ 「版本」**仍然指向课本**（不是版次）—— 2026-10-16 之前它就是"课本"的别名，
   *    老粘贴文本里写「版本」的意思是"人教版"这种人话。把它改指版次会让**老文本
   *    静默变成另一本书**（"人教版"被当成版次，课本名却空了），那是数据事故。
   *    版次的别名见下面那一组（版次 / 印刷版次 / edition）。
   */
  版本: 'textbook',
  // ---- 版次（**已不用**：认得这一列、读进来忽略、不报错）----
  edition: 'edition',
  版次: 'edition',
  出版版次: 'edition',
  印刷版次: 'edition',
  第几版: 'edition',
  grade: 'grade',
  年级: 'grade',
  semester: 'semester',
  term: 'semester',
  学期: 'semester',
  册: 'semester',
  fromtextbook: 'fromTextbook',
  from_textbook: 'fromTextbook',
  词语表: 'fromTextbook',
  是否词语表: 'fromTextbook',
  来自词语表: 'fromTextbook',
  // 这一步收录在哪张表（识字表 / 写字表 / 词语表）
  listtype: 'listType',
  list_type: 'listType',
  表: 'listType',
  字表: 'listType',
  收录表: 'listType',
  所属表: 'listType',
  识字表: 'listType',
  写字表: 'listType',
};

/** 学年 code ↔ 中文年级（含"三上"这种简写）。 */
const GRADE_ALIASES: Record<string, string> = {
  primary_1: 'primary_1',
  primary_2: 'primary_2',
  primary_3: 'primary_3',
  primary_4: 'primary_4',
  primary_5: 'primary_5',
  primary_6: 'primary_6',
  junior_1: 'junior_1',
  junior_2: 'junior_2',
  junior_3: 'junior_3',
  senior_1: 'senior_1',
  senior_2: 'senior_2',
  senior_3: 'senior_3',
  一年级: 'primary_1',
  二年级: 'primary_2',
  三年级: 'primary_3',
  四年级: 'primary_4',
  五年级: 'primary_5',
  六年级: 'primary_6',
  初一: 'junior_1',
  初二: 'junior_2',
  初三: 'junior_3',
  初一一年级: 'junior_1',
  高一: 'senior_1',
  高二: 'senior_2',
  高三: 'senior_3',
  七年级: 'junior_1',
  八年级: 'junior_2',
  九年级: 'junior_3',
  1: 'primary_1',
  2: 'primary_2',
  3: 'primary_3',
  4: 'primary_4',
  5: 'primary_5',
  6: 'primary_6',
};

/** 学年 code 的中文数字（"3" → "三"），用于把"3年级"归一成"三年级"。 */
const DIGIT_TO_CN: Record<string, string> = {
  '1': '一',
  '2': '二',
  '3': '三',
  '4': '四',
  '5': '五',
  '6': '六',
};

// ---------------------------------------------------------------- 归一工具

/** 全角 ASCII → 半角（含全角逗号、全角空格、全角数字）。 */
export function toHalfWidth(text: string): string {
  return text.replace(/[\uFF01-\uFF5E]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0));
}

/** 单元格归一：去 BOM/零宽字符、全角转半角、去首尾空白。 */
export function normalizeCell(raw: string): string {
  return toHalfWidth(raw.replace(/[\uFEFF\u200B-\u200D]/g, ''))
    .replace(/\u3000/g, ' ')
    .trim();
}

/** 去掉包裹的引号（Excel 导出的 `"秋天"`）。 */
function stripQuotes(text: string): string {
  if (text.length >= 2) {
    const first = text[0];
    const last = text[text.length - 1];
    if ((first === '"' && last === '"') || (first === "'" && last === "'")) {
      return text.slice(1, -1).trim();
    }
  }
  return text;
}

/**
 * 把一行切成单元格。
 *
 * 用**优先级**而不是"按出现次数投票"选分隔符：制表符 > 竖线 > 逗号 >
 * 2 个以上空格 > 顿号。理由是这几类分隔符在实际粘贴内容里的可信度依次下降
 * （逗号可能出现在"备注,注意"里，顿号则可能出现在词语列表本身里）。
 *
 * ⚠️ `tokens`（**按表录入**的单列词元行）是一条特例，见下面那段注释。
 */
function splitCells(
  line: string,
  tokens?: readonly string[],
): { cells: string[]; delimiter: DelimiterName } {
  /**
   * ⚠️ **按表录入**（"一行就是这一张表的词元"）时的特例：
   *
   * 用户要求"录制或者录词都可以用**空格或者是制表符（就是tab键）**分隔"，
   * 而且一行里的词元**都是同一类东西**（要么全是单字、要么全是词），
   * 于是这里可以把 **Tab 与空格一视同仁地当分隔符** —— `天<TAB>地 人` 是三个词元。
   *
   * ⛔ 这条特例**只在"这一行的表类型已知、而且整行确实是词元行"时生效**
   *    （判据见 `parseDictationImport` 里的 `looksLikeListTokenLine`）。
   *    通用整块导入里 Tab 是**列**分隔符（`课文 | 字 | 词 | 拼音 | …`），
   *    那些行有自己的列语义，不能被摊平。
   */
  if (tokens !== undefined) {
    return { cells: [...tokens], delimiter: line.includes('\t') ? 'tab' : 'spaces' };
  }
  if (line.includes('\t')) {
    return { cells: line.split('\t'), delimiter: 'tab' };
  }
  if (line.includes('|')) {
    return { cells: line.split('|'), delimiter: 'pipe' };
  }
  if (line.includes(',') || line.includes('，')) {
    return { cells: line.split(/[,，]/), delimiter: 'comma' };
  }
  // 顿号优先于单个空格：中文里顿号就是并列词语的分隔符
  if (line.includes('、')) {
    return { cells: line.split('、'), delimiter: 'ideographic' };
  }
  if (/\s{2,}/.test(line)) {
    return { cells: line.split(/\s{2,}/), delimiter: 'spaces' };
  }
  // 单个空格既可能是"词表分隔符"（`秋天 天气 叶子`），也可能出现在**课文标题内部**
  // （`第5课 秋天`、`小小的船 月儿`）。这里用一个保守规则消歧：
  //   · 形如课序（`第5课 秋天` / `5 秋天` / `识字1 日月明`）→ 整行当一个单元格；
  //   · 其余含空格的行 → 按空格拆成多个单元格（词表）。
  // 判据只认"首列像课序"，所以 `秋天 天气` 仍然被当词表（两行词）。
  if (/[\s\u3000]/.test(line.trim())) {
    const trimmed = line.trim();
    if (looksLikeLessonHeader(trimmed)) {
      return { cells: [trimmed], delimiter: 'spaces' };
    }
    return { cells: trimmed.split(/[\s\u3000]+/), delimiter: 'spaces' };
  }
  return { cells: [line], delimiter: 'spaces' };
}

/**
 * 这一行是不是**「按表录入」的词元行**？是的话返回它的词元数组，否则返回
 * `undefined`（说明这一行要按**列**理解，交给 `splitCells` 的列规则）。
 *
 * 判据（**只认这一种形状**，收紧到不会和别的贴法打架）：
 *   1. 这一行不含 `|` / 逗号（那些是**列**分隔符，写了就说明它是列式写法）；
 *   2. 这一行**有内容**，而且已知"录的是哪张表"（`listType !== null`）；
 *   3. 按 `splitTokens`（**空格与 Tab 都当分隔符**）摊平之后，**每个词元都能进
 *      这张表**（字表：恰好一个汉字；词语表：2 个及以上汉字）。
 *
 * 于是：
 *   · `天 地 人`（识字表）→ 三个词元，认；
 *   · `天地 人口`（词语表）→ 两个词元，认；
 *   · `天 天地`（识字表）→ `天地` 进不了字表 ⇒ **返回 undefined**，
 *     这一行走 `splitCells` 的列规则：第 2 格是"词"，于是由 `buildRows` 的
 *     准入规则**逐词元**报错（这正是要的行为）；
 *   · `天地\t人口\t拼音`（带表头的列式粘贴）→ 第一个词元 `天地` 进不了
 *     **字**表 ⇒ 返回 undefined，按 Tab 分列（列语义保住）。
 */
function looseTokensOf(line: string, listType: ListType | null): string[] | undefined {
  if (listType === null) return undefined;
  if (line.includes('|') || line.includes(',') || line.includes('，')) return undefined;
  const trimmed = line.trim();
  if (trimmed === '') return undefined;
  const tokens = splitTokens(trimmed);
  if (tokens.length === 0) return undefined;
  if (!tokens.every((token) => checkListToken(listType, token) === null)) return undefined;
  return tokens;
}

/** 取一行的"主要分隔符"，用于给整份文本报一个统一的 delimiter。 */
function dominantDelimiter(lines: string[], listType: ListType | null): DelimiterName {
  const counts: Record<DelimiterName, number> = {
    tab: 0,
    comma: 0,
    spaces: 0,
    pipe: 0,
    ideographic: 0,
  };
  for (const line of lines) {
    if (isHanziRun(line)) {
      counts.spaces += 1;
      continue;
    }
    /**
     * ⚠️ 与主循环用**同一个判据**（`looseTokensOf`）取"这一行是不是按表录入的
     *    词元行"，否则报告里说的分隔符会与真正用的那一个不一致。
     */
    if (looseTokensOf(line, listType) !== undefined) {
      counts[line.includes('\t') ? 'tab' : 'spaces'] += 1;
      continue;
    }
    counts[splitCells(line).delimiter] += 1;
  }
  let best: DelimiterName = 'spaces';
  let bestCount = -1;
  for (const key of Object.keys(counts) as DelimiterName[]) {
    // 平票时按 tab > pipe > comma > spaces > ideographic 的优先级取
    if (counts[key] > bestCount) {
      best = key;
      bestCount = counts[key];
    }
  }
  return best;
}

/**
 * 判断一行是否是"用单个空格或顿号隔开的**纯汉字串**"，例如：
 *
 *     秋 气 了 子 人 大
 *     秋、气、了、子、人、大
 *
 * 这是从课本配套表格/Word 文档里粘贴**生字表**时最常见的形状（每个字之间
 * 只有一个空格）。它和"字 词 拼音"这种列式写法长得像，所以必须单独认：
 * 列式写法里除了首列，其余列**不可能全是单字**（拼音是拉丁字母、类别是中文词）。
 *
 * 认出来的行会被拆成多个「只有字」的行（见 `expandHanziRun`）。
 */
export function isHanziRun(line: string): boolean {
  if (line.includes('\t') || line.includes('|') || line.includes(',') || line.includes('，')) {
    return false;
  }
  const tokens = line.split(/[\s\u3000、]+/).filter((t) => t !== '');
  if (tokens.length < 2) return false;
  return tokens.every((t) => [...t].length === 1 && isHanzi(t));
}

/** 把"纯汉字串"行拆成单字数组。 */
export function expandHanziRun(line: string): string[] {
  return line
    .split(/[\s\u3000、]+/)
    .map((t) => t.trim())
    .filter((t) => [...t].length === 1 && isHanzi(t));
}

/** 是否是汉字（BMP 的 CJK 统一表意文字）。 */
export function isHanzi(ch: string): boolean {
  const code = ch.codePointAt(0) ?? 0;
  return code >= 0x4e00 && code <= 0x9fff;
}

// ---------------------------------------------------------------- 三张表的「准入规则」（逐词元）
//
// 这一节的四个函数是**唯一口径**：解析器的每一条分支（表名行展开 / 单列词元行 /
// 单元格）都用它们判定"这个词元能不能进这张表"。写成导出的纯函数是为了让
// 测试可以**不用整段文本**就把规则钉死（见 `dictation-list-tokens.test.ts`）。

/**
 * 表类型的**中文短名**（报错文案里用）。
 *
 * ⚠️ 用 `LIST_TYPE_SHORT_LABELS`（`listing.ts`）的值，但不直接依赖它：
 *    报错文案是给人看的，改一个字不该牵动"表类型 → 名字"的映射表。
 */
const LIST_TYPE_CN: Record<ListType, string> = {
  shizi: '识字表',
  xiezi: '写字表',
  cibiao: '词语表',
};

/** 一个词元里的字符数（按码点，不是 UTF-16 码元）。 */
function charCountOf(token: string): number {
  return [...token].length;
}

/** 这个词元是不是"恰好一个汉字"。 */
export function isSingleHanziToken(token: string): boolean {
  const chars = [...token];
  return chars.length === 1 && isHanzi(chars[0]!);
}

/**
 * 这个词元能不能作为**字表**（识字表 / 写字表）的一项？返回 `null` = 可以，
 * 否则返回**给人看的**原因。
 *
 * 规则（用户原话："录这两个表的时候，只能录单个的汉字"）：
 *   · 恰好一个汉字 → 可以；
 *   · 多字（如 `天地`）→ 不行（那是词，属于词语表）；
 *   · 单个非汉字（标点、字母、数字）→ 不行。
 *
 * ⚠️ 空词元是"没有内容"，不是"违规" —— 由调用方折叠掉（见 `splitTokens`）。
 */
export function checkHanziListToken(token: string, listType?: HanziListType | null): string | null {
  const table = listType != null ? LIST_TYPE_CN[listType] : '识字表 / 写字表';
  const chars = [...token];
  if (chars.length === 1 && isHanzi(chars[0]!)) return null;
  if (chars.length === 1) {
    return `词元「${token}」不是汉字（标点、字母、数字都不能作为生字）——「${table}」里只能录单个的汉字。`;
  }
  return (
    `词元「${token}」是 ${chars.length} 个字符 ——「${table}」里必须是单个汉字，不能录词语。` +
    `要录词语请放到「词语表」，或保存后在「浏览与编辑」里点这个字、在弹出框里给它造词。`
  );
}

/**
 * 这个词元能不能作为**词语表**的一项？返回 `null` = 可以，否则返回原因。
 *
 * 规则（用户原话："如果是词语表，那就相反，只能录词语或者是成语"）：
 *   · **2 个及以上汉字** → 可以（这是"词语或成语"的最低门槛）；
 *   · 单个汉字 → 不行。
 *
 * ⚠️ 这里**不做**"1 个字就是生字，帮你记成生字"的静默改类别 —— 那正是本项目
 *    最忌讳的事（家长以为录了一个词，库里却多出一个字）。
 */
export function checkWordListToken(token: string): string | null {
  const chars = [...token];
  if (chars.length < 2) {
    return (
      `词元「${token}」只有 1 个汉字 ——「词语表」里只能录词语或成语（2 个及以上汉字），` +
      `不能录单个的字。要录生字请放到「识字表 / 写字表」。`
    );
  }
  if (chars.length > 16) {
    return `词元「${token}」是 ${chars.length} 个字符，超过了词语的长度上限（16 个汉字）。`;
  }
  if (!chars.every((c) => isHanzi(c))) {
    return `词元「${token}」里含非汉字字符（标点、字母、数字都不能作为词语）。`;
  }
  return null;
}

/**
 * 按"这一行录的是哪张表"判定一个词元（`null` = 可以录）。
 *
 * `listType` 为 `null` = **不知道录的是哪张表**（通用整块导入没写表名）。
 * 此时**不做**准入判定 —— 通用入口里 `字` / `词` 是两列，收不收由下面按列的
 * 校验负责（见文件头「三张表各自能录什么」那一节的 ⚠️）。
 */
export function checkListToken(listType: ListType | null, token: string): string | null {
  if (listType === null) return null;
  if (listType === 'cibiao') return checkWordListToken(token);
  return checkHanziListToken(token, listType);
}

// ---------------------------------------------------------------- 连着写的汉字（2026-10-16 用户实测）

/**
 * **字表的自动拆字**：这一行如果"没有任何分隔符、而且整行都是汉字"，就把每个汉字
 * 当成一个词元返回；否则返回 `null`（说明走既有的逐词元判定）。
 *
 * ## 为什么需要它（用户实测）
 *
 * 家长照课本后面的《识字表》粘进来的形状就是**连着写**：
 *
 *     天地人你我他
 *     一二三四五上下
 *
 * 以前这一行是**一个词元**（6 个字符）⇒ 逐词元报"识字表里必须是单个汉字"，
 * 于是家长录不了字。而家长的意图清清楚楚：这一行就是 6 个生字。
 *
 * ## 判据为什么这么收紧
 *
 *   · `listType` **只认字表**（识字表 / 写字表）：词语表**绝不能**这样拆 ——
 *     `天地人口` 是 1 个词还是 2 个词，解析器**判断不出来**，猜就是静默错数据
 *     （见 `ambiguousWordRun` 给的那句人话报错）；
 *   · "整行都是汉字"**本身就等价于"没有任何分隔符"**（空格 / Tab / 逗号 / 顿号
 *     / 竖线都不是汉字），所以不必再单独判一次分隔符；
 *   · 只要掺了标点、字母、数字就返回 `null` ⇒ 一个字都不拆、**按既有规则逐词元报错**
 *     （"这段里全是汉字"是这一段能被拆的前提，不替家长清理他自己写的标点）；
 *   · 单个汉字返回 `null`：它本来就是**一个**词元，没有"拆"这回事。
 *
 * 拆出来的每个字都还会**照常过** `checkListToken`（`buildStrictTokenRows` 里逐个判），
 * 所以"每个词元恰好一个汉字"这条准入规则**一点都没放宽**。
 */
export function autoSplitHanziRun(line: string, listType: ListType | null): string[] | null {
  if (listType !== 'shizi' && listType !== 'xiezi') return null;
  const text = line.trim();
  if (text === '') return null;
  const chars = [...text];
  if (chars.length < 2) return null;
  if (!chars.every((ch) => isHanzi(ch))) return null;
  return chars;
}

/**
 * 词语表里"连着写、一个分隔符都没有"的汉字串，**几个汉字**才算"判断不出词边界"。
 *
 * `2`：词语表要求每个词 ≥2 个汉字，所以**两个汉字连着写时"一个词"是唯一合法的读法**
 *      （拆成两个单字在词语表里本来就不合法）⇒ 不歧义，照旧收下
 *      （"一行一个词（换行）也认"这条既有录法靠它保住）；
 * `3` 及以上：既可能是一个词、也可能是几个词、还可能是一串**生字**
 *      （正好粘错了表）⇒ 判断不出来，**报错让人确认，绝不猜**。
 */
export const WORD_RUN_AMBIGUOUS_MIN = 3;

/**
 * **词语表的"连着写"**：这一行只有一个连着写的汉字串、中间没有任何分隔符，
 * 而且长到**判断不出词从哪里断开**时，返回那个串；否则返回 `null`。
 *
 * ⚠️ 与 `autoSplitHanziRun` 是**互斥的两件事**：那边是"帮你拆开"，这边是"不猜、报错"。
 */
export function ambiguousWordRun(line: string, listType: ListType | null): string | null {
  if (listType !== 'cibiao') return null;
  const text = line.trim();
  if (text === '') return null;
  const chars = [...text];
  if (chars.length < WORD_RUN_AMBIGUOUS_MIN) return null;
  if (!chars.every((ch) => isHanzi(ch))) return null;
  return text;
}

/**
 * 词语表"连着写"的**报错文案**（人话：说清为什么不行 + 两条出路）。
 *
 * 用户要求（原话）："要给出明确的人话报错（说明词语表必须用空格或 Tab 分开，
 * 因为无法判断词从哪里断开）"，并给出建议（"如果你想录 3 个生字，请切到识字表；
 * 如果想录词，请用空格分开"）。
 */
export function describeAmbiguousWordRun(token: string): string {
  const n = [...token].length;
  return (
    `词元「${token}」是 ${n} 个汉字连着写的、中间没有任何分隔符 ——「词语表」里` +
    `无法判断词从哪里断开（它既可能是一个词，也可能是几个词，还可能是 ${n} 个生字，` +
    `三种读法都说得通，所以不替你猜）。词语表必须用空格或 Tab 把词分开，` +
    `例如写成「天地 人口」这样、一个词一个词元。` +
    `如果你想录的是 ${n} 个生字，请切到「识字表 / 写字表」；` +
    `如果确实只录这一个词，请用「浏览与编辑 → 新增词语」手工加。`
  );
}

/**
 * 字表自动拆字的**汇总提示**（"注意"那一栏里显示，⚠️ 不许静默拆开）。
 *
 * 用户要求（原话）："界面上要看得见这件事（例如预览里的『注意』或一行说明：
 * 『有 N 行没有分隔符，已按单个汉字自动拆开』），不要静默。"
 *
 * 一行一条会刷屏（课本后面一张表就是几十行），所以**汇总成一条**，
 * 并把行号列出来（太多时只列前 12 行）。
 */
export function describeAutoSplitHanziRun(lines: readonly number[]): string {
  const shown = lines.slice(0, 12).join('、');
  const more = lines.length > 12 ? ` 等（共 ${lines.length} 行）` : '';
  return (
    `有 ${lines.length} 行没有分隔符，已按单个汉字自动拆开（第 ${shown} 行${more}）。` +
    `识字表 / 写字表里汉字可以连着写；如果某一行本来是一个词，请切到「词语表」、` +
    `并用空格或 Tab 把词分开（词语表里连着写是判断不出词边界的）。`
  );
}

/** 这个词元是不是"恰好一个拼音音节"（`tiān` / `zhǎng` / `cháng`）。 */
export function isSingleSyllableToken(token: string): boolean {
  return isPinyinSyllable(token);
}

/**
 * 这一格是不是**纯拼音**（`cháng` / `cháng jiāng`）？
 *
 * ⚠️ 只在"带表头的列式行"里用它判"`词` 这一列写的是整词拼音还是词"：
 *    用户明确要求"拼音列要照旧允许"，而 `词,拼音` 表头下第 2 格是**拼音列**，
 *    把 `cháng jiāng` 当成"含非汉字的词"报错是**假错误**。
 */
export function isPinyinCell(cell: string): boolean {
  const parts = cell.trim().split(/[\s\u3000]+/).filter((part) => part !== '');
  if (parts.length === 0) return false;
  return parts.every((part) => isPinyinSyllable(part));
}

/**
 * 把一行里的"词元单元格"摊平成词元数组：**空格（含全角）与 Tab 都是分隔符**。
 *
 * 用户要求（2026-10-16）："录制或者录词都可以用空格或者是制表符（就是tab键）分隔"。
 * 在"一行里的词元都是同一类东西"这条规则下（见文件头），空格不再有歧义 ——
 * 所以这里可以放心地把空格与 Tab 一视同仁。
 *
 * 归一化（都由这一步吃掉，不让空词元流到下游）：
 *   · 行首 / 行尾空白 → 丢掉；
 *   · **连续空格**（`天   地`）→ 一个分隔符；
 *   · **全角空格**（U+3000）、BOM、零宽字符 → 与半角空格等价（走 `normalizeCell`）；
 *   · Tab 在**行内任何位置**都当分隔符（Excel 粘出来就是它）。
 */
export function splitTokens(cell: string): string[] {
  return normalizeCell(cell)
    .split(/[\t\u3000 ]+/)
    .map((token) => token.trim())
    .filter((token) => token !== '');
}

/** 中文数字（一到十）→ 阿拉伯数字，供课序解析。 */
const CN_NUM: Record<string, number> = {
  一: 1,
  二: 2,
  三: 3,
  四: 4,
  五: 5,
  六: 6,
  七: 7,
  八: 8,
  九: 9,
  十: 10,
};

/**
 * 解析课序。认这些写法：
 *   `5` / `5*` / `第5课` / `第 5 课` / `识字1` / `语文园地三` / `3.5`
 * 返回 `{ no, label }`：`no` 是数字部分（认不出为 0），`label` 是原始写法。
 */
export function parseLessonNo(raw: string): { no: number; label: string } {
  const label = stripQuotes(normalizeCell(raw));
  if (label === '') return { no: 0, label: '' };
  const arabic = /(\d+)/.exec(label);
  if (arabic?.[1]) return { no: Number.parseInt(arabic[1], 10), label };
  const cn = /([一二三四五六七八九十]+)/.exec(label);
  if (cn?.[1]) {
    const digits = cn[1];
    // 只处理"十以内"（课本课序不会超过十的写法）：十一、二十这类按阿拉伯数字写即可
    const value = CN_NUM[digits];
    if (value !== undefined) return { no: value, label };
  }
  return { no: 0, label };
}

/** 归一学期：上 / 下 / 不分。认不出来返回 null（由调用方报错）。 */
export function normalizeSemester(raw: string): string | null {
  const text = normalizeCell(raw);
  if (text === '') return null;
  if (text === '不分' || text === '全' || text === '-') return '不分';
  if (text.startsWith('上')) return '上';
  if (text.startsWith('下')) return '下';
  return null;
}

/** 归一学年：`三年级` / `3年级` / `primary_3` / `三` 都认。认不出返回 null。 */
export function normalizeGrade(raw: string): string | null {
  const text = normalizeCell(raw);
  if (text === '') return null;
  const direct = GRADE_ALIASES[text];
  if (direct !== undefined) return direct;
  // "3年级" → "三年级"
  const digit = /^([1-9])\s*年级?$/.exec(text);
  if (digit?.[1] && DIGIT_TO_CN[digit[1]]) {
    return GRADE_ALIASES[`${DIGIT_TO_CN[digit[1]]}年级`] ?? null;
  }
  return null;
}

/**
 * 归一"是否来自课本词语表"的真假值。
 *
 * ⚠️ 刻意**不**把 `x` 当成"是"：中文习惯里 `√` 表示对、`×` 表示错，
 * 而英文习惯里 x 又常被写成"选中"。两者冲突时按中文习惯来（本产品面向中文家长），
 * 把 `√ / ✓ / v` 当"是"，`× / x` 当"否"。
 */
export function normalizeBoolean(raw: string): boolean | null {
  const text = normalizeCell(raw).toLowerCase();
  if (text === '') return null;
  if (['1', 'true', 'yes', 'y', '是', '✓', '√', 'v'].includes(text)) return true;
  if (['0', 'false', 'no', 'n', '否', '×', 'x', '-'].includes(text)) return false;
  // "词语表" / "课本词语表" 这种写法也表示"是"
  if (text.includes('词语表')) return true;
  return null;
}

// ---------------------------------------------------------------- 表头识别

/** 把表头单元格归一成内部字段名（认不出返回 null）。 */
function headerField(cell: string): FieldKey | null {
  const key = normalizeCell(cell).toLowerCase().replace(/[\s_\-（）()]/g, '');
  return HEADER_ALIASES[key] ?? null;
}

/** 判断第一行是否是表头（至少 2 个单元格能认出来，或含"字/词/拼音"这类关键字）。 */
function looksLikeHeader(cells: string[]): boolean {
  if (cells.length < 2) return false;
  const recognized = cells.filter((c) => headerField(c) !== null).length;
  return recognized >= 2;
}

// ---------------------------------------------------------------- 主解析

/**
 * 依据表头把一行映射成字段值。
 *
 * `columns[i] === null` 表示"这一列的表头名不认识" —— 该列被忽略，
 * 而不是当成某个默认字段（把备注列当成年级会让整批数据报一堆莫名其妙的错）。
 */
function mapWithHeader(
  cells: string[],
  columns: (FieldKey | null)[],
): Partial<Record<FieldKey, string>> {
  const values: Partial<Record<FieldKey, string>> = {};
  for (let i = 0; i < columns.length && i < cells.length; i++) {
    const field = columns[i];
    if (field === null || field === undefined) continue;
    const value = stripQuotes(normalizeCell(cells[i] ?? ''));
    if (value !== '') values[field] = value;
  }
  return values;
}

/**
 * 一行（单个单元格）看起来像"课文表头"吗？`第5课 秋天` / `5 秋天` / `识字1 日月明` 这类。
 *
 * 用来把课文行与单词条区分开：`秋天` 是词条，`第5课 秋天` 不是。
 * 判据（满足其一即可）：
 *   · 含"第…课" / "识字…" / "语文园地…" 这些课本里**固定**的课序写法；
 *   · 以阿拉伯数字开头，且**后面还有别的字**（`5 秋天`）——
 *     纯粹的 `5` 不算（那是一个数字，不是课题）。
 */
function looksLikeLessonHeader(text: string): boolean {
  if (/^(第\s*[0-9一二三四五六七八九十]+|识字\s*[0-9一二三四五六七八九十]+|语文园地\s*[一二三四五六七八九十]+)/.test(text)) {
    return true;
  }
  return /^[0-9]+\s*\*?\s*[.、:：]?\s*\S+/.test(text) && !/^[0-9]+$/.test(text);
}

/**
 * 解析课文单元格。认这些写法：
 *   `秋天`            → 课题"秋天"，课序缺失
 *   `第1课 秋天`       → 课序"第1课"，课题"秋天"
 *   `1 秋天` / `1.秋天` / `1、秋天` / `1:秋天`
 *   `识字1 日月明`     → 课序"识字1"
 *   `语文园地三`       → 课序"语文园地三"（无课题，用课序当课题）
 */
export function parseLessonCell(raw: string): { title: string; label: string; no: number } {
  const text = stripQuotes(normalizeCell(raw));
  if (text === '') return { title: '', label: '', no: 0 };
  const split =
    /^(第?\s*[0-9一二三四五六七八九十]+\s*\*?\s*课?|识字\s*[0-9一二三四五六七八九十]+|语文园地\s*[一二三四五六七八九十]+)\s*[.、:：]?\s*(.+)$/.exec(
      text,
    );
  if (split?.[1] !== undefined && split[2] !== undefined && split[2] !== '') {
    const label = split[1].trim();
    const title = split[2].trim();
    return { title, label, no: parseLessonNo(label).no };
  }
  return { title: text, label: '', no: parseLessonNo(text).no };
}

/**
 * 无表头时按默认列序映射（制表符 / 逗号 / 竖线这类"表格粘贴"用它）。
 * 只有一列的行（例如整行就一个词）也由它处理 —— 那一列就是"字或词"，
 * 具体是字还是词由 `buildRow` 的"单字即字、多字即词"规则判定。
 */
function mapByPosition(cells: string[]): Partial<Record<FieldKey, string>> {
  const values: Partial<Record<FieldKey, string>> = {};
  for (let i = 0; i < DEFAULT_COLUMNS.length && i < cells.length; i++) {
    const field = DEFAULT_COLUMNS[i]!;
    const value = stripQuotes(normalizeCell(cells[i] ?? ''));
    if (value !== '') values[field] = value;
  }
  return values;
}

/** 一行归一后的原始单元格（都已去引号与空白）。 */
interface RawCells {
  char: string;
  word: string;
  lesson: string;
  no: string;
  unit: string;
  textbook: string;
  /** 版次（**已不用**；旧文本里若有这一列，读进来只为忽略）。 */
  edition: string;
  grade: string;
  semester: string;
  category: string;
  pinyin: string;
  fromTextbook: string;
  listType: string;
}

/** 从"字段值映射"里取出各单元格（缺的算空串）。 */
function pickCells(values: Partial<Record<FieldKey, string>>): RawCells {
  const get = (key: FieldKey): string => stripQuotes(normalizeCell(values[key] ?? ''));
  return {
    char: get('char'),
    word: get('word'),
    lesson: get('lesson'),
    no: get('no'),
    unit: get('unit'),
    textbook: get('textbook'),
    /**
     * ⚠️ 版次走 `normalizeEdition`（不只 `normalizeCell`）：全角数字、连续空白、
     *    汉字之间的排版空格都要在**解析这一层**消掉，否则 `２０２４年８月第1版`
     *    会与 `2024年8月第1版` 存成两本书。
     */
    edition: normalizeTextbookText(stripQuotes(normalizeCell(values['edition'] ?? ''))),
    grade: get('grade'),
    semester: get('semester'),
    category: get('category'),
    pinyin: get('pinyin'),
    fromTextbook: get('fromTextbook'),
    listType: get('listType'),
  };
}

/** 一行的所有单元格都为空（单列词表行用它当底）。 */
function emptyCells(): RawCells {
  return {
    char: '',
    word: '',
    lesson: '',
    no: '',
    unit: '',
    textbook: '',
    edition: '',
    grade: '',
    semester: '',
    category: '',
    pinyin: '',
    fromTextbook: '',
    listType: '',
  };
}

/** `buildRows` 的入参。 */
interface RowInput {
  cells: RawCells;
  /** 上一行设定的课文上下文（没有则为 null）。 */
  context: LessonContext | null;
  line: number;
  raw: string;
  /** 没有课文信息时是否发警告（词表行只在第一行提示一次，避免刷屏）。 */
  warnNoLesson: boolean;
  /**
   * 是否是"单列形状"（没有表头、整行没有列分隔）：此时**一个字的单元格算"字"**。
   * 表格形状（有表头或制表符）里"词"列写一个字仍然是词 —— 家长在词条里写
   * 单字词（如"秋"）是合法的，不能擅自改成生字。
   */
  singleColumnShaped: boolean;
  /**
   * 这一行**没有自己写明表类型**时用的表类型（表名上下文行 / 请求默认值）。
   * `null` = 都不知道 —— 此时字行不写 `listType`（服务层会按"这本书没有指定
   * 表类型"处理，而不是替家长猜一张表）。
   */
  listTypeFallback: ListType | null;
  /**
   * 文本里没有写类别时，词条用的默认类别（"整段就是一份词语表粘贴"时用它）。
   * `null` = 交给服务层的 `toWordCategory()`。
   */
  wordCategoryFallback: string | null;
  /**
   * 调用方给的**默认课本**（"按书 + 按表录入"时界面上选好的那一本）。
   *
   * ⚠️ 它**不是**"缺了就自动建一个"——课本按册建，缺年级/学期时由服务层
   *    报逐行错误（解析器这一层不知道册别）。
   * ⛔ `defaultEdition` 已废弃（2026-10-18）：版次不用了，只为旧调用方兼容。
   */
  defaultTextbook: string | null;
  defaultEdition: string | null;
  errors: ImportIssue[];
  warnings: ImportIssue[];
  /**
   * **列式行撞上准入规则**时回调一次（"这一行没收下"）。
   *
   * ⚠️ 存在的理由只有一个：让预览里那张账的恒等式成立 ——
   *    这一行既然没落库，就必须在 `problem` 上 +1，并把已经记成"收下"的
   *    那几行**撤销**（参数 = 这次 `buildRows` 产出、随后被滤掉的行数）。
   *    逐词元那种录法**不走**这个回调（它由 `buildStrictTokenRows` 逐 token 记账）。
   */
  strictReject?: ((droppedRows: number) => void) | undefined;
  /**
   * 表头里有「表」这一列 ⇒ 这一行的结构是**列式**的。
   *
   * ⚠️ 用它把 `multiCell` 判据关掉：`识字表 | 天地 | 2024年8月第1版` 里
   *    "料"只有一格（`字` 列），其余是属性列 —— 不能按"三个词元"判
   *    （否则连版次那一格都会被报成"识字表里不能录词语"）。
   */
  listTypeColumn?: boolean | undefined;
}

/**
 * 用**统一的取词条目**（cells）构造一行数据。任何字段不合法就抛错
 * （错误信息就是 `Error.message`），由调用方决定"这一行算不算废掉"。
 *
 * 这个函数存在的意义：导入数据有**两种来源**（表格列 / 单列的词表行），
 * 它们的校验规则必须逐字相同 —— 否则"表格里的词"和"词表里的词"会被
 * 不同尺度地接受或拒绝，家长根本猜不出自己哪里写错了。
 */
function buildRows(input: RowInput): ParsedImportRow[] {
  const { cells, context, line, raw, errors, warnings } = input;

  const charRaw = cells.char;
  const wordRaw = cells.word;
  let lessonTitle = '';
  let lessonLabel = '';
  let lessonNo = 0;
  let unitNo: number | undefined;

  if (cells.lesson !== '') {
    const parsed = parseLessonCell(cells.lesson);
    lessonTitle = parsed.title;
    lessonLabel = parsed.label;
    lessonNo = cells.no !== '' ? parseLessonNo(cells.no).no : parsed.no;
  } else if (context) {
    lessonTitle = context.lessonTitle;
    lessonLabel = context.lessonLabel;
    lessonNo = context.lessonNo;
    unitNo = context.unitNo;
  } else if (cells.no !== '') {
    const parsed = parseLessonNo(cells.no);
    lessonNo = parsed.no;
    lessonLabel = parsed.label;
  }
  if (cells.unit !== '' && unitNo === undefined) {
    unitNo = Number.parseInt(cells.unit, 10) || undefined;
  }

  /**
   * 先校验"整行共享的字段"（课文之外的那些列），**一次把所有问题都收集起来**。
   *
   * 为什么要收集而不是"遇到第一个就抛"：家长一份粘贴里最常见的错就是
   * 年级写成了"大学"这种，如果一次只报一个字段，他得改一轮再跑一轮预览 ——
   * 而预览本身就是"先看清楚再导入"的手段，一次报全才是它的意义。
   */
  const sharedErrors: string[] = [];
  let rowTextbook: string | undefined;
  let rowGrade: string | undefined;
  let rowSemester: string | undefined;
  let rowFromTextbook: boolean | undefined;
  /**
   * 这一行落在哪张表。**只认"表名列写出来的表类型"**：
   *   本行的表类型列 > 表名上下文行 > 调用方默认值。
   * ⚠️ 刻意**不**从 `类别` 列的文字里推（那会让一行 OCR 噪声改掉整批数据的归属）。
   */
  let rowListType: ListType | undefined;
  /**
   * `rowListType` 是不是**"我们明确知道这一行录的是哪张表"**。
   *
   * ⚠️ 它与"有 `rowListType`"**不是同一件事**：`类别` 列写 `1类字` 时也会推出
   *    写字表，但那是**通用整块导入**里的"字 + 词"两列写法
   *    （`竹 | 竹叶` 是"这个字 + 由它组成的词"）—— 那种行**不能**按
   *    "字表里不许出现词"来判，否则老粘贴文本会被整批拒绝/静默少录。
   *    只有"表名列 / 表名上下文行 / 调用方 `defaultListType`"才算明确知道。
   */
  let rowListStrict = false;
  {
    const tb =
      cells.textbook !== ''
        ? cells.textbook
        : (context?.textbook ?? input.defaultTextbook ?? '');
    if (tb !== '') rowTextbook = tb;
    /**
     * 版次（`cells.edition`）**认得但不再使用**（2026-10-18）。
     *
     * 旧文本里可能还有「版次」列；这里**不报错、不校验、不写进数据** ——
     * 用户手里那批文本不该因为这一列突然失败。课本的身份是"出版社 + 年级 + 学期"。
     */
    if (cells.listType !== '') {
      const parsedListType = toListType(cells.listType);
      if (parsedListType === null) {
        sharedErrors.push(
          `无法识别的表：「${cells.listType}」（只能是 识字表 / 写字表 / 词语表）`,
        );
      } else {
        rowListType = parsedListType;
        rowListStrict = true;
      }
    } else if (cells.category !== '') {
      /**
       * 老文本的兼容路径（**只在没有表名列时**走）：
       * `1类字` / `会写字` / `type1` = 会写 = 写字表，`2类字` / `会认字` = 识字表。
       * 改造前 `类别` 列就是这个口径（`hanzi.category`），把老粘贴里的归属
       * 静默丢掉是不可接受的。
       *
       * ⚠️ 它排在"表名列"之后、"默认值"之前：家长明说了表就以他说的为准。
       * ⚠️ 但 **`rowListStrict` 仍然保持 false** —— 这一行是通用导入的列式写法，
       *    `字 | 词` 两列同时有值是它的正常形状（见 `rowListStrict` 的说明）。
       */
      const fromCategory = toListType(cells.category);
      rowListType = fromCategory ?? (input.listTypeFallback ?? undefined);
    } else if (input.listTypeFallback !== null) {
      rowListType = input.listTypeFallback;
      rowListStrict = true;
    }
    if (cells.grade !== '') {
      const grade = normalizeGrade(cells.grade);
      if (grade === null) sharedErrors.push(`无法识别的年级：「${cells.grade}」`);
      else rowGrade = grade;
    }
    if (cells.semester !== '') {
      const semester = normalizeSemester(cells.semester);
      if (semester === null) {
        sharedErrors.push(`无法识别的学期：「${cells.semester}」（只能是 上/下/上册/下册/不分）`);
      } else rowSemester = semester;
    }
    if (cells.fromTextbook !== '') {
      const flag = normalizeBoolean(cells.fromTextbook);
      if (flag === null) {
        sharedErrors.push(`「词语表」列只能是 是/否（或 1/0），这里是「${cells.fromTextbook}」`);
      } else rowFromTextbook = flag;
    }
  }

  /** 一份"公共部分"（课文归属等），字行与词行共用。 */
  const base = (): ParsedImportRow => {
    if (sharedErrors.length > 0) throw new Error(sharedErrors.join('；'));
    const row: ParsedImportRow = { line, raw, contextOnly: false };
    if (lessonTitle !== '') {
      row.lessonTitle = lessonTitle;
      row.lessonLabel = lessonLabel;
      row.lessonNo = lessonNo;
      if (unitNo !== undefined) row.unitNo = unitNo;
    }
    if (rowTextbook !== undefined) row.textbook = rowTextbook;
    if (cells.category !== '') row.category = cells.category;
    else if (input.wordCategoryFallback !== null) row.category = input.wordCategoryFallback;
    if (rowGrade !== undefined) row.gradeCode = rowGrade;
    if (rowSemester !== undefined) row.semester = rowSemester;
    if (rowFromTextbook !== undefined) row.fromTextbook = rowFromTextbook;
    if (rowListType !== undefined) row.listType = rowListType;
    return row;
  };

  const out: ParsedImportRow[] = [];
  const pushWithLessonWarning = (row: ParsedImportRow): void => {
    if (row.lessonTitle === undefined && input.warnNoLesson) {
      warnings.push({
        line,
        raw,
        message: '没有课文信息（既没写课文列，前面也没有课文行），这一行会按「无课文」导入',
      });
    }
    out.push(row);
  };

  /**
   * ---- 三张表各自的准入规则（**逐词元**，2026-10-16 用户决定）----
   *
   * 见文件头「三张表各自"能录什么"」。这一段的全部要点：
   *
   *   · 识字表 / 写字表 → 每个词元**恰好一个汉字**；
   *   · 词语表 → 每个词元**2 个及以上汉字**（词语或成语）；
   *   · `rowListStrict === false`（通用整块导入：`字` / `词` 是**两列**）→ **不判**，
   *     由下面按列的校验负责（老粘贴文本里的"字 + 词"两列不能被静默拒掉）。
   *
   * ## 违规的词元：**逐个报错，那一个不收**
   *
   *   · **每一个**违规词元单独报一条（带 `token`），家长一眼看到是哪几个；
   *   · 违规的那一个**不产生数据行**；
   *   · "一行多个词元"的录法（`天 地 人` / `天地 人口`）由
   *     `buildStrictTokenRows` 逐词元处理 → **合法的照常入库**（用户认可的省事口径；
   *     去重保证重粘不会产生重复行）。
   *   · **列式行**（`字 | 词 | 拼音`，即"一行里有多个**列**"）不同：两个列都写了却
   *     有一个违规，说明这一行的**结构**本身写错了（把词放进了字表），
   *     此时**整行不收** —— 半行数据比没有数据更难核对。
   *
   * ⚠️ 报错要**精确到词元**（`token` 字段）：家长一行可能粘了 20 个字元，
   *    只说"第 4 行有问题"等于没给信息。
   */
  if (rowListStrict && rowListType !== undefined) {
    const strictListType: ListType = rowListType;
    const offenders: { token: string; message: string }[] = [];
    const collect = (cell: string): boolean => {
      let allOk = true;
      for (const token of splitTokens(cell)) {
        const reason = checkListToken(strictListType, token);
        if (reason === null) continue;
        allOk = false;
        offenders.push({ token, message: reason });
      }
      return allOk;
    };
    /**
     * ⚠️ **列式行**（这一行有多个"格"）与**单列词元行**要分开判：
     *
     *   · 单列（`天 天地`）：整格就是"词元列"，逐词元判；
     *   · 列式（`字 | 词 | 拼音` / `天<TAB>天地`）：`字` 那一列只要**有值**，
     *     在这一行就是**错位的列**（字表里不该出现"字 + 词"两列）→ 整行拒。
     *     这一条必须**先判**，否则 `识字表 | 天地 | 2024年8月第1版` 这种
     *     "表名列 + 别的内容"会被当成"三个词元"，报出一堆莫名其妙的错。
     *
     *     ⚠️ 例外：**"字/词"只有一格、其余的格都是属性列**（年级/学期/表/课本/版次）
     *     时，它就是**单列词元行**（家长把"录的是哪张表/哪个年级"写在列里，
     *     内容写在一格里）。所以先看"料"有几格 —— 判据只用"字/词两列是不是
     *     都写了"，**不看整行有几个分隔符**（`识字表 | 天地 | 版次` 里有
     *     两个 Tab，但"料"只有一格）。
     */
    const multiCell = input.listTypeColumn !== true && charRaw !== '' && wordRaw !== '';
    let charOk = true;
    let wordOk = true;
    if (charRaw !== '' && multiCell) {
      offenders.push({
        token: charRaw,
        message:
          `「${LIST_TYPE_CN[strictListType]}」里只能录${strictListType === 'cibiao' ? '词语或成语' : '单个的汉字'}` +
          `（一行可以写多个，用空格或 Tab 分开）——这里是「${charRaw}」，看起来是**列式写法**。` +
          '按表录入请把内容放在一格/一列里（例如 `天 地 人`）；一行里写多列（`课文 | 字 | 词`）不是这张表的写法。',
      });
      charOk = false;
    } else {
      charOk = charRaw === '' ? true : collect(charRaw);
    }
    const wordIsPinyin = strictListType === 'cibiao' && wordRaw !== '' && isPinyinCell(wordRaw);
    wordOk = wordRaw === '' || wordIsPinyin ? true : collect(wordRaw);
    for (const offender of offenders) {
      errors.push({ line, raw, token: offender.token, message: offender.message });
    }
    if (!charOk || !wordOk) {
      /**
       * 整行不收。**账**按"这一行（不是逐词元）"记：
       *   · `problem` 加 1（`strictReject`，一个调用方）；
       *   · `recognized` **减掉**这一次没收下的那些行 —— 调用方在
       *     `buildRows` 返回后按返回的行数记过账了，这里把被滤掉的撤销。
       * 于是 `tokens = recognized + problem` 对**列式行**逐行成立
       * （逐词元那种录法由 `buildStrictTokenRows` 逐 token 记账，两条路互斥）。
       */
      input.strictReject?.(out.length);
      const filtered =
        !charOk && !wordOk
          ? []
          : charOk
            ? out.filter((row) => row.wordText === undefined)
            : out.filter((row) => row.charText === undefined);
      out.length = 0;
      out.push(...filtered);
      return out;
    }
  }

  /**
   * 逐项校验：**字与词各自独立**。
   *
   * 为什么不能"整行一起抛错"：一行里同时写了字与词（`竹 竹叶 zhú`）时，
   * 如果词的拼音有问题就把整行丢掉，那个**完全正确的字**也会跟着消失 ——
   * 家长的感受是"我明明填对了，它却说这一行有错，而且什么都没导入"。
   * 所以：哪一项有问题就只跳哪一项，错误照样逐条报出来。
   *
   * ⚠️ 这只适用于**通用整块导入**（`字` / `词` 是两列）。"按表录入"下
   *    一行里不允许混放字与词，那一条由上面那段准入规则拦掉。
   */
  const attempt = (fn: () => void): void => {
    try {
      fn();
    } catch (err) {
      errors.push({
        line,
        raw,
        message: err instanceof Error ? err.message : '这一行无法解析',
      });
    }
  };

  // ---- 汉字 ----
  if (charRaw !== '') {
    attempt(() => {
      /**
       * ⚠️ **词语表里不能录单个汉字**（2026-10-16 用户规则）。
       *
       * 这里的走法只有一种：**按表录入 · 词语表** + 第 2 格写了字
       * （`天地\t人` → 第 3 格是"字"）。通用整块导入的 `字` 列不受影响
       * （`rowListType === undefined`）。
       */
      if (rowListStrict && rowListType === 'cibiao') {
        throw new Error(
          `「词语表」里只能录词语或成语（2 个及以上汉字），不能录单个的字 —— 这里是「${charRaw}」。` +
            '要录生字请放到「识字表 / 写字表」；要给某个字造词，请保存后在「浏览与编辑」里点这个字、在弹出框里录。',
        );
      }
      const reason = checkHanziListToken(
        charRaw,
        isHanziListType(rowListType ?? '') ? (rowListType as HanziListType) : null,
      );
      if (reason !== null) throw new Error(reason);
      const row = base();
      row.charText = charRaw;
      if (cells.pinyin !== '') {
        /**
         * 字与词写在同一行时，拼音列**只作用于字**（词的拼音按字拼，见文档）。
         * 但如果这一行的字与词都在、而拼音写成多音节（`竹 竹叶 zhú yè`），
         * 那它显然是**词的拼音**，交给下面的词语分支 —— 否则家长会遇到
         * "明明写对了却报错"。
         */
        const multiSyllable = /\s/.test(cells.pinyin);
        const matchesWord = wordRaw !== '' && validateWordPinyin(wordRaw, cells.pinyin).ok;
        if (multiSyllable && !matchesWord) {
          throw new Error(`单个汉字的拼音只能有一个音节，这里写成了「${cells.pinyin}」`);
        }
        if (!multiSyllable) row.pinyin = cells.pinyin;
      }
      /**
       * ⚠️ 只在**表类型已知**（表名列 / 表名上下文行 / 调用方默认值）时补：
       *    那时这一行才会写进 `hanzi_listing`，才有"这个位置的读音"可填。
       *    通用整块导入（`字 | 词` 两列、没写表）**不动** —— 不替家长猜一张表。
       */
      if (rowListStrict) fillAutoReadingForChar(row, rowListType ?? null);
      pushWithLessonWarning(row);
    });
  }

  // ---- 词语 ----
  if (wordRaw !== '') {
    /**
     * 单列形状（"生字表/词表"那两行）里，一个字的单元格算"字"而不是"词"；
     * 表格形状（有表头或制表符）里，"词"列写一个字也是合法的词（如"秋"作词条）。
     *
     * ⚠️ **录的是「词语表」时不再走这条**（2026-10-16 用户规则）：那正是
     *    "悄悄把单个字当生字"的老行为，本项目最忌讳。此时由上面的准入规则
     *    逐词元报错（"「词语表」里只能录词语或成语"）。
     */
    const treatAsChar =
      charRaw === '' &&
      [...wordRaw].length === 1 &&
      input.singleColumnShaped &&
      !rowListStrict;
    attempt(() => {
      if (treatAsChar) {
        const row = base();
        row.charText = wordRaw;
        if (cells.pinyin !== '') row.pinyin = cells.pinyin;
        pushWithLessonWarning(row);
        return;
      }
      const chars = [...wordRaw];
      if (chars.length < 1 || chars.length > 16) {
        throw new Error(`「词」列长度必须在 1~16 字之间，这里是 ${chars.length} 字`);
      }
      if (!chars.every((c) => isHanzi(c))) {
        throw new Error(`词「${wordRaw}」里含非汉字字符（标点、字母、数字都不行）`);
      }
      const row = base();
      row.wordText = wordRaw;
      if (cells.pinyin !== '') {
        const check = validateWordPinyin(wordRaw, cells.pinyin);
        if (!check.ok) throw new Error(`词语拼音有误：${check.reason}`);
        row.pinyin = cells.pinyin;
      }
      pushWithLessonWarning(row);
    });
  }

  return out;
}

/** 解析课文单元格得到的上下文。 */
interface LessonContext {
  lessonTitle: string;
  lessonLabel: string;
  lessonNo: number;
  unitNo?: number;
  textbook?: string;
  /**
   * 上下文行里写明的**版次**（`课本	版次	年级	学期` 这种表头的"只有课本"行）。
   * 下面的数据行没写版次时继承它。
   */
  edition?: string;
}

/**
 * 解析整块粘贴文本。
 *
 * 这个函数**不做任何数据库判断**（不知道哪些已存在），只做：
 * 切分、归一、逐行校验、把"课文表头行"识别出来。
 * "新增 / 已存在"的判定在服务层（需要查库）。
 */
/**
 * 「表名行」里那一格**整格**能认的表名（归一后**必须整格相等**）。
 *
 * ## 为什么这里不能用 `toListType()` 的宽松包含匹配（2026-10-22 实测）
 *
 * 「按书录入」的文件格式里，**词语表**有一个词就叫「写字」：
 *
 * ```text
 * 写字,xiě zì	丛林,cóng lín	深处,shēn chù	竹林,zhú lín	熊猫,xióng māo	朋友,péng you
 * ```
 *
 * `toListType('写字,xiě zì')` 先去掉空格、再 `includes('写字')` ⇒ 认出「写字表」
 * ⇒ 整行被当成**表名行**：`写字` 被当标题吃掉、同行其余 5 个词按**写字表**规则
 * 报「必须是单个汉字」的假错误。真实文件（二年级上册-词语表.txt，225 个词）
 * 就是这样变成「219 个词 + 5 条假错误 + 1 个词静默消失」的。
 *
 * 所以表名行**只认整格就是表名**（允许后面带一个括号说明，例如
 * `识字表（一上）`）。宽松匹配仍然保留给**列值**（`类别` / `表` 列里的
 * 「1类字」「会写字」「词语」等）—— 那里的宽容是历史数据的兼容，见 `toListType()`。
 */
const LIST_MARKER_NAMES: Record<string, ListType> = {
  识字表: 'shizi',
  认字表: 'shizi',
  写字表: 'xiezi',
  生字表: 'xiezi',
  词语表: 'cibiao',
  词表: 'cibiao',
  shizi: 'shizi',
  xiezi: 'xiezi',
  cibiao: 'cibiao',
};

/** 单元格里的表类型标记（"识字表" / "写字表" / "词语表"）→ 表类型。 */
function cellListType(cell: string): ListType | null {
  const text = stripQuotes(normalizeCell(cell)).replace(/[\s\u3000]/g, '');
  if (text === '') return null;
  const direct = LIST_MARKER_NAMES[text];
  if (direct !== undefined) return direct;
  /**
   * `识字表（一上）` 这种"表名 + 括号说明"以前靠宽松匹配认得；
   * 收窄成整格相等时不能把它弄丢（括号已被 `normalizeCell()` 归一成半角）。
   */
  const withNote = /^(识字表|认字表|写字表|生字表|词语表|词表)[(][^)]*[)]$/.exec(text);
  return withNote?.[1] !== undefined ? (LIST_MARKER_NAMES[withNote[1]] ?? null) : null;
}

/** 单元格是不是`字:读音` / `字（读音）`这种"给某个字注音"的写法。 */
function charReadingCell(cell: string): { char: string; reading: string } | null {
  const text = stripQuotes(normalizeCell(cell));
  const matched =
    /^([\u4e00-\u9fff])\s*[:：]\s*([a-z\u00e0-\u01dc\u1e00-\u1eff]+)$/i.exec(text) ??
    /^([\u4e00-\u9fff])\s*[（(]\s*([a-z\u00e0-\u01dc\u1e00-\u1eff]+)\s*[）)]$/i.exec(text);
  if (matched?.[1] === undefined || matched[2] === undefined) return null;
  return { char: matched[1], reading: matched[2].toLowerCase() };
}

/**
 * 两个读音是不是**同一个音**。
 *
 * 先各自走写库那一套归一（`normalizePinyinSyllable()`：`shuang1` → `shuāng`、
 * `Shuāng` → `shuāng`），归一成功就比归一后的值；归一失败（不该发生，调用方
 * 一般已经校验过）就退回"去空白 + 小写"的字面比较 —— 不因为一个奇怪的输入
 * 把两处**明明一样**的拼音判成"不一致"。
 */
function isSamePinyin(left: string, right: string): boolean {
  const a = normalizePinyinSyllable(left);
  const b = normalizePinyinSyllable(right);
  if (a.ok && b.ok) return a.reading === b.reading;
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

/** 文案里显示用的拼音：能归一就归一，不能就原样（只用于「文件里写的是…」）。 */
function displayPinyin(raw: string): string {
  const checked = normalizePinyinSyllable(raw);
  return checked.ok ? checked.reading : raw.trim();
}

/**
 * **单音字一律用字典拼音；凡是「文件拼音 ≠ 字典拼音」→ 待确认**（2026-10-21，B 方案）。
 *
 * 用户原话（验收标准）：
 * > "用 B 比较好，核对几十个，好过全部核一遍。那些'和字典不一致'的依然要用底色呈现。"
 * > "单音字 → 字典的拼音为准（我的 OCR 声调不可信）"
 *
 * 于是这一层的口径是（**只对字表**：识字表 / 写字表）：
 *
 * | 文件里写了拼音 | 字典里几个读音 | 落库用的读音 | 待确认？ |
 * | --- | --- | --- | --- |
 * | 没写 | 恰好 1 个 | 字典那个音 | 否（这条是既有行为） |
 * | 没写 | 0 或 ≥2 个 | 空着（不猜） | 否 |
 * | 写了 | 恰好 1 个 | **字典那个音**（覆盖文件里的） | 文件 ≠ 字典 ⇒ 是 |
 * | 写了 | ≥2 个 | **文件写的那个音**（既有行为，不变） | 文件 ≠ 字典首音 ⇒ 是 |
 * | 写了 | 0 个（内置表没收录） | 文件写的那个音 | 否（没有字典可比） |
 *
 * ⚠️ **多音字绝不改成字典音**：`长` 读 cháng 还是 zhǎng 由课本决定，字典首音
 *    只是"核对时的参照"。落库仍然用文件写的那个（没写就留空）。
 * ⚠️ "待确认"复用既有的 `pinyinUncertain`（与会与文件里的 `?` 合并）；原因分开记
 *    在 `pinyinMismatch` / `pinyinMarkedUncertain` 上，好让文案说清是哪种。
 * ⚠️ 词语表（`cibiao`）与表类型未知（`null`）**不碰** —— 词的读音是**整词**的，
 *    不是"每个字一个音"。
 */
function fillAutoReadingForChar(row: ParsedImportRow, listType: ListType | null): void {
  if (row.charText === undefined) return;
  if (listType !== 'shizi' && listType !== 'xiezi') return;

  const readings = availableReadings(row.charText);
  /** 字典拼音 = 第一个读音（主读音）。0 个读音时没有可对照的字典值。 */
  const dictionary = readings[0] ?? '';
  const fileReading = (row.pinyin ?? '').trim();

  /** 文件里没写拼音：只有单音字才补（多音字、未收录的字一律留空）。 */
  if (fileReading === '') {
    if (readings.length !== 1) return;
    const checked = normalizePinyinSyllable(dictionary);
    if (!checked.ok) return;
    row.pinyin = checked.reading;
    return;
  }

  /** 文件里写了拼音：先如实记下"文件写的是什么"（文案与比对都要它）。 */
  row.filePinyin = displayPinyin(fileReading);

  /** 内置表里没这个字：没有字典值可比，保持文件写的那个（既有行为）。 */
  if (dictionary === '') return;

  if (!isSamePinyin(fileReading, dictionary)) {
    row.pinyinUncertain = true;
    row.pinyinMismatch = true;
  }

  /**
   * 单音字：**这个位置的读音就是字典那一个**（不论文本里写的是什么）。
   * 多音字：保持文件写的那个音（⛔ 不改，见上面的表）。
   */
  if (readings.length === 1) {
    const checked = normalizePinyinSyllable(dictionary);
    row.pinyin = checked.ok ? checked.reading : dictionary;
  }
}

/**
 * 老写法 `天 tiān`（拼音词元跟在字后面）也走**同一套**口径。
 *
 * 为什么不能只改 `fillAutoReadingForChar()`：那条路径是"字先建行、拼音后来"，
 * 建行时还没有拼音（单音字已经被自动填成字典音），等拼音词元到了再把
 * `row.pinyin` 直接盖上去，就绕过了上面的比对 —— 单音字会被文件里的错音覆盖，
 * 也不会标待确认。所以这里单独一个入口，规则与 `fillAutoReadingForChar()` 一致。
 */
function applyFileReadingForChar(row: ParsedImportRow, reading: string): void {
  if (row.charText === undefined) return;
  const fileReading = reading.trim();
  if (fileReading === '') return;
  row.filePinyin = displayPinyin(fileReading);

  const readings = availableReadings(row.charText);
  const dictionary = readings[0] ?? '';
  if (dictionary === '') {
    row.pinyin = fileReading;
    return;
  }
  if (!isSamePinyin(fileReading, dictionary)) {
    row.pinyinUncertain = true;
    row.pinyinMismatch = true;
  }
  if (readings.length === 1) {
    const checked = normalizePinyinSyllable(dictionary);
    row.pinyin = checked.ok ? checked.reading : dictionary;
    return;
  }
  row.pinyin = fileReading;
}

/**
 * 「表名行」：第一个单元格**整格就是一个表类型**（见 `LIST_MARKER_NAMES`）。
 *
 * 两种形态：
 *   · 只有表名（`识字表`）→ 表名成为**上下文**（下面的行都属于这张表）；
 *   · 表名后面还跟着内容（`识字表<TAB>天<TAB>地<TAB>人`）→ 直接展开成数据行，
 *     **不**产生上下文（避免一行噪声改掉后面所有行的归属）。
 *
 * ⚠️ **必须整格相等**，不能用 `toListType()` 的宽松包含匹配：词语表里就有
 *    「写字」这个词（`写字,xiě zì`），宽松匹配会把整行当表名行吃掉
 *    （2026-10-22 的真实文件回归，见 `LIST_MARKER_NAMES` 的注释）。
 *
 * 返回 `null` 表示"这一行不是表名行"。
 */
export function parseListMarkerRow(cells: readonly string[]): {
  listType: ListType;
  rest: string[];
} | null {
  const first = cells[0];
  if (first === undefined) return null;
  const listType = cellListType(first);
  if (listType === null) return null;
  const rest = cells
    .slice(1)
    .map((cell) => stripQuotes(normalizeCell(cell)))
    .filter((cell) => cell !== '');
  return { listType, rest };
}

/** `parseDictationImport` 的选项（全部可省略）。 */
export interface ParseImportOptions {
  /**
   * 文本里没有表名、也没有表名列时用的默认表类型。
   * 省略 = 不知道（字行不写 `listType`，服务层按"没指定表"处理，**不替家长猜**）。
   */
  defaultListType?: ListType | null;
  /**
   * 文本里没有写类别时，词条的默认类别（`common` / `phrase` / `idiom`）。
   * 省略 = 交给服务层的 `toWordCategory()`（认不出来时按 `phrase`）。
   */
  defaultWordCategory?: string | null;
  /**
   * 文本里没有写「课本」列时用的**默认课本**（界面上选的那本书）。
   *
   * ⚠️ 课本是"一册"（出版社 + 年级 + 学期）：年级 / 学期由行上的列
   *    （或服务层的默认值）决定；**版次已经不需要**（2026-10-18）。
   */
  defaultTextbook?: string | null;
  /**
   * 文本里没有写「版次」列时用的**默认版次**。
   *
   * ⛔ **已废弃（2026-10-18）**：版次彻底不用了（不进身份、不上界面、不写库）。
   *    这个入参只为"旧调用方传了也别炸"而保留 —— 传了也只是写进内存里的
   *    `row.edition` 元数据，**没有任何下游会读它**。
   *    缺版次也**不再报错**（旧文本照原样粘）。
   */
  defaultEdition?: string | null;
}

/**
 * 解析整块粘贴文本。
 *
 * 这个函数**不做任何数据库判断**（不知道哪些已存在），只做：
 * 切分、归一、逐行校验、把"课文表头行"与"表名行"识别出来。
 * "新增 / 已存在"的判定在服务层（需要查库）。
 */
export function parseDictationImport(
  text: string | null | undefined,
  options: ParseImportOptions = {},
): ParseImportResult {
  /**
   * ⚠️ 第一次就遇到 `text` 是 `null/undefined`（例如调用方把 `body.text` 传丢）
   * 时，报一句**人话**比抛 `Cannot read properties of undefined (reading 'replace')`
   * 有用得多 —— 后者在日志里完全看不出是"哪一份输入缺了正文"。
   */
  if (typeof text !== 'string') {
    throw new TypeError('parseDictationImport 需要一段文本（收到的是 null/undefined）');
  }
  const defaultListType = options.defaultListType ?? null;
  const defaultWordCategory =
    options.defaultWordCategory !== undefined && options.defaultWordCategory !== null
      ? options.defaultWordCategory
      : null;
  /** 调用方给的默认课本 / 默认版次（都已归一；空串当没给）。 */
  const defaultTextbook = normalizeTextbookText(options.defaultTextbook ?? '');
  const defaultEdition = normalizeTextbookText(options.defaultEdition ?? '');
  const errors: ImportIssue[] = [];
  const warnings: ImportIssue[] = [];
  const rows: ParsedImportRow[] = [];
  const textbooks: string[] = [];

  const rawLines = text.replace(/\r\n?/g, '\n').split('\n');
  const nonEmpty = rawLines.map((l) => l.trim()).filter((l) => l !== '');
  const delimiter = dominantDelimiter(nonEmpty, defaultListType);

  // ---- 1) 表头 ----
  /**
   * `header[i] === null` = 这一列的表头名不认识（忽略该列，不当成默认字段）。
   *
   * ⚠️ 表头**在逐行解析里认**（见主循环那段说明）："按表录入"的文本形状是
   *    `识字表` → `字,拼音` → `长,cháng`，表头在第 2 行。
   */
  let header: (FieldKey | null)[] = [];
  let headerRowIndex = -1;
  let headerRaw: string[] = [];

  // ---- 2) 逐行解析 ----
  /**
   * 上下文：课文（`第1课 秋天`）与**表名**（`识字表`）共用一条"继承链"。
   * 表名有它自己的来源（`currentListType`），两者互相独立 —— 先有表名再有课文、
   * 或者先有课文再有表名，都是家长的正常写法。
   */
  let context: LessonContext | null = null;
  /** 当前作用中的表名（表名上下文行设定，遇到下一个表名行才换）。 */
  let currentListType: ListType | null = defaultListType;

  const stats = {
    totalLines: rawLines.length,
    blankLines: 0,
    contextLines: 0,
    charRows: 0,
    wordRows: 0,
    bothRows: 0,
    /**
     * 用户"录了多少个"的那个数：**空格 / Tab / 换行切出来的词元（token）数**。
     *
     * ⚠️ 恒等式（预览里会给家长核对，见 `summarizeImportCounts`）：
     *    `tokens = recognized + problem`
     *    —— 每个词元要么变成一行数据，要么进"有问题"那一栏，**不能两边都不算**。
     *    所以这里**不逐个累加**，而是在整趟解析结束时由那两个数求出（见 `resolve` 之前）。
     */
    tokens: 0,
    /** 收下的词元数（正常等于 `dataRowCount`）。 */
    recognized: 0,
    /** 没收下的词元数（字表里写词 / 词语表里写单字）。 */
    problem: 0,
  };

  /** 违规词元去重（同一条文案不重复报），但计数按**出现次数**。 */
  const invalidTokenSeen = new Set<string>();
  /**
   * 被**自动按单个汉字拆开**的那些行（见 `autoSplitHanziRun`）。
   *
   * ⚠️ 不许静默：整趟解析结束后汇总成一条"注意"（见 `describeAutoSplitHanziRun`），
   *    否则家长看到"识别出 75 个字"却不知道自己哪几行是被系统替他拆的。
   */
  const autoSplitLines: number[] = [];
  /** 登记"这一行收下了 N 个词元"。 */
  const markRecognized = (count = 1): void => {
    stats.recognized += count;
  };
  const checkToken = (
    listType: ListType | null,
    token: string,
    lineNo: number,
    raw: string,
    /**
     * **覆盖判定结果的原因**（`undefined` = 走 `checkListToken` 的常规判定）。
     *
     * 目前只有一处用它：词语表里"连着写的汉字串"（`ambiguousWordRun`）——
     * 那一串按"每个词元 ≥2 个汉字"是**合法**的，但它在**这一行的形状**下
     * 判断不出词边界，所以要报一条不一样的错，而**记账/去重/报错**这条路径
     * 必须与别的违规词元完全一致（否则预览里那张账会对不上）。
     */
    reasonOverride?: string,
  ): boolean => {
    const reason = reasonOverride ?? checkListToken(listType, token);
    if (reason === null) return true;
    stats.problem += 1;
    const dedupeKey = `${lineNo}\u0000${token}`;
    if (!invalidTokenSeen.has(dedupeKey)) {
      invalidTokenSeen.add(dedupeKey);
      errors.push({ line: lineNo, raw, token, message: reason });
    }
    return false;
  };

  for (let i = 0; i < rawLines.length; i++) {
    const lineNo = i + 1;
    const original = rawLines[i]!;
    const line = original.trim();
    if (line === '') {
      stats.blankLines += 1;
      continue;
    }
    if (i === headerRowIndex) continue;

    /** 收下一行数据（登记课本引用 + 统计）。 */
    const markRow = (row: ParsedImportRow): void => {
      if (row.textbook !== undefined && !textbooks.includes(row.textbook)) {
        textbooks.push(row.textbook);
      }
      if (row.charText !== undefined && row.wordText !== undefined) stats.bothRows += 1;
      else if (row.charText !== undefined) stats.charRows += 1;
      else if (row.wordText !== undefined) stats.wordRows += 1;
      rows.push(row);
    };

    /**
     * **按表录入的单列词元行**：逐个词元判定，**合法的照常收下、违规的逐条报错**。
     *
     * 这是用户明确要的行为（"分多次录入、重复不产生重复"的前提下，最省事的口径）：
     *
     *   · `天 天地`（识字表）→ 「天」录进去；「天地」报错并**不产生数据**；
     *   · `天地 人`（词语表）→ 「天地」录进去；「人」报错并不产生数据。
     *
     * 于是 `词元数 = 收下的 + 违规的` 在**每一条**这样的行上逐 token 成立
     * （预览里那张账的恒等式靠它）。
     *
     * 返回 `true` = 这一行已经处理完（调用方 `continue`）。
     *
     * `cellTexts`：显式给"这一行有哪些格"（列式写法用）——不给就按
     * `splitTokens(line)`（空格 / Tab 混用）摊平。两种都会**再按空格/Tab**
     * 摊平一次，因为一个格里也可能写了两个词元（`识字表<TAB>天 地`）。
     */
    const buildStrictTokenRows = (
      listType: ListType,
      cellTexts?: readonly string[] | undefined,
    ): boolean => {
      if (listType !== 'shizi' && listType !== 'xiezi' && listType !== 'cibiao') return false;
      const tokens =
        cellTexts === undefined
          ? splitTokens(line)
          : cellTexts.flatMap((cell) => splitTokens(cell));
      if (tokens.length === 0) return false;

      /** 这一行已经收下的字行（用来接收跟在后面的**拼音**词元）。 */
      let lastCharRow: ParsedImportRow | null = null;

      for (const token of tokens) {
        /**
         * ⚠️ **老写法的兼容**（`长:cháng` / `长（cháng）`）：这一格本身就是
         *    "字 + 读音"的合体写法。先把它拆开再判定 —— 否则这一格会因为
         *    "含非汉字字符"被拒，家长以前能粘的写法就**静默变成一行错误**
         *    （本项目最忌讳的事）。
         */
        const inline = charReadingCell(token);
        const candidate = inline !== null ? inline.char : token;

        /**
         * ⚠️ **拼音词元**：字表里 `长<TAB>cháng` 是"给这个字标读音"的合法写法
         *    （用户要求"拼音列要照旧允许"）。它**不是**一个"要录的汉字"，
         *    所以既不报错、也不单独占用一行 —— 挂到上一个字行上。
         *
         *    判据收得很紧（三条同时成立），避免把正常的字/词误吞掉：
         *      ① 上一个词元刚收下一个**字**；
         *      ② 这一段是**一个音节形状**（只有小写字母/声调符号）；
         *      ③ 而且**确实是这个字的读音之一**（内置字表的主读音或备选读音）。
         *
         *    ③ 是关键：没有它，`天 a` 里的 `a` 会被当成"天读 a"（它确实是
         *    一个音节形状）而**静默吞掉一个错误**。
         */
        if (
          inline === null &&
          (listType === 'shizi' || listType === 'xiezi') &&
          lastCharRow !== null &&
          lastCharRow.charText !== undefined &&
          isSingleSyllableToken(token) &&
          isReadingOfChar(lastCharRow.charText, token)
        ) {
          /**
           * ⚠️ 走 `applyFileReadingForChar()`，**不要**直接 `= token`：
           *    单音字必须仍然落字典音，而且"文件写的和字典不一样"要标待确认。
           */
          applyFileReadingForChar(lastCharRow, token);
          continue;
        }
        if (!checkToken(listType, candidate, lineNo, line)) {
          lastCharRow = null;
          continue;
        }
        /** 字表里能收下的词元一定是单个汉字（`checkListToken` 保证了），按**字**录。 */
        const asChar = listType !== 'cibiao' && isSingleHanziToken(candidate);
        const row: ParsedImportRow = {
          line: lineNo,
          raw: line,
          contextOnly: false,
          listType,
        };
        if (asChar) row.charText = candidate;
        else row.wordText = candidate;
        if (inline !== null) row.pinyin = inline.reading;
        fillAutoReadingForChar(row, listType);
        if (context !== null) {
          row.lessonTitle = context.lessonTitle;
          row.lessonLabel = context.lessonLabel;
          row.lessonNo = context.lessonNo;
          if (context.unitNo !== undefined) row.unitNo = context.unitNo;
          if (context.textbook !== undefined) row.textbook = context.textbook;
          if (context.edition !== undefined) row.edition = context.edition;
        }
        markRecognized();
        markRow(row);
        lastCharRow = asChar ? row : null;
      }
      return true;
    };

    /**
     * ---- **文件格式**：一行 = 课本一行，词元是 `汉字,拼音`（2026-10-20）----
     *
     * 用户把课本后面的三张表交给助手生成 .txt，然后上传到「按书录入」：
     *
     *     霜,shuāng	吹,chuī	落,luò	降,jiàng
     *     氏,shì	什,shén	李,lǐ	张,zhāng
     *
     * 这一条与 `buildStrictTokenRows` 是**同一套准入规则、同一套记账**，区别只有两点：
     *
     *   1. 词元自带拼音（`字,拼音`），所以**不需要**"整份按两列 + 表头"那种写法；
     *   2. 拼音要**逐音节校验并归一**（`shuang1` → `shuāng`），写进这一行的
     *      `pinyin` —— 服务层会把它存成 `hanzi_listing.reading`（= 这个位置的读音），
     *      预览显示、朗读都用它，**不再靠同音字去猜**。
     *
     * 用户明确的两条口径：
     *   · 文件里**带了拼音 = 已经标音** ⇒ 重复出现的字**不再要求手工标音**；
     *   · 但**同字同音仍然要拦**（同一张表里同一个字两次都写 `shén`）—— 那是
     *     `checkListingReadings()` 的事，这里只负责把拼音**如实**放进行里。
     *
     * ⚠️ 拼音不合法时：**逐行报错**（带词元），那一个词元不产生数据；
     *    同一行里合法的词元照常收下（与逐词元报错的老口径一致）。
     */
    const buildReadingTokenRows = (listType: ListType, tokens: readonly ReadingToken[]): void => {
      for (const token of tokens) {
        const textReason = checkListToken(listType, token.text);
        if (textReason !== null) {
          checkToken(listType, token.text, lineNo, line, textReason);
          continue;
        }

        const row: ParsedImportRow = { line: lineNo, raw: line, contextOnly: false, listType };
        if (listType === 'cibiao') row.wordText = token.text;
        else row.charText = token.text;

        if (token.pinyin !== null) {
          if (listType === 'cibiao') {
            const checked = normalizeWordPinyin(token.text, token.pinyin);
            if (!checked.ok) {
              checkToken(
                listType,
                token.text,
                lineNo,
                line,
                `「${token.text}」的拼音「${token.pinyin}」有问题：${checked.reason}。` +
                  '合法写法：带声调符号（cháng jiāng）、声调数字（chang2 jiang1）或不带声调（chang jiang）。',
              );
              continue;
            }
            row.pinyin = checked.reading;
          } else {
            if (/[\s\u3000-]/.test(token.pinyin)) {
              checkToken(
                listType,
                token.text,
                lineNo,
                line,
                `「${token.text}」是单个汉字，拼音只能有一个音节，这里写成了「${token.pinyin}」` +
                  '（多个音节是词语的拼音，请放到「词语表」；或检查是不是多写了空格/连字符）。',
              );
              continue;
            }
            const checked = normalizePinyinSyllable(token.pinyin);
            if (!checked.ok) {
              checkToken(
                listType,
                token.text,
                lineNo,
                line,
                `「${token.text}」的拼音「${token.pinyin}」不是合法的普通话音节：${checked.reason}。` +
                  '合法写法：带声调符号（shuāng）、声调数字（shuang1）或不带声调（shuang）。',
              );
              continue;
            }
            row.pinyin = checked.reading;
          }
        }
        /**
         * 读音的最终口径（2026-10-21）：单音字用字典音；文件拼音与字典不一致的
         * 标成待确认。见 `fillAutoReadingForChar()` 的对照表。
         */
        fillAutoReadingForChar(row, listType);
        /**
         * 「不确定」标记（`得,dé?`）：**只有字表**往下传 —— 预览里字卡要标黄、
         * 保存前要那道「待确认」校验拦一下。
         *
         * ⚠️ 词语表不传：那里没有"每个字一个位置"的确认入口，传下去会让一批词
         *    永远保存不了（标了 `?` 也只好当格式标记剥掉）。
         * ⚠️ 放在 `fillAutoReadingForChar()` **之后**：`字,?`（没写拼音）也该被
         *    标成待确认 —— 自动补的那个音同样是"没经过人确认"的。
         * ⚠️ 与"文件拼音 ≠ 字典拼音"分开记原因（`pinyinMarkedUncertain`）：
         *    两者都算待确认，但给用户看的文案不同（见 `checkListingReadings()`）。
         */
        if (token.uncertain && listType !== 'cibiao') {
          row.pinyinUncertain = true;
          row.pinyinMarkedUncertain = true;
        }

        if (context !== null) {
          row.lessonTitle = context.lessonTitle;
          row.lessonLabel = context.lessonLabel;
          row.lessonNo = context.lessonNo;
          if (context.unitNo !== undefined) row.unitNo = context.unitNo;
          if (context.textbook !== undefined) row.textbook = context.textbook;
          if (context.edition !== undefined) row.edition = context.edition;
        }
        markRecognized();
        markRow(row);
      }
    };

    /**
     * ---- 「表名行」：`识字表` / `写字表` / `词语表`（可以有内容跟在后面）----
     *
     * ⚠️ 这一条必须在"纯汉字串行"之前判：`识字表` 本身是三个汉字，
     *    会被 `isHanziRun` 当成"三个生字"（那是**静默错数据**：识字表这三个字
     *    被当成生字导进写字表）。
     */
    {
      const marker = parseListMarkerRow(line.split(/[\t|]/).map((cell) => normalizeCell(cell)));
      if (marker !== null) {
        if (marker.rest.length === 0) {
          currentListType = marker.listType;
          stats.contextLines += 1;
          warnings.push({
            line: lineNo,
            raw: line,
            message: `这一行是表名「${LIST_TYPE_SHORT_LABELS[marker.listType]}」——下面的行都按这张表录入`,
          });
          rows.push({
            line: lineNo,
            raw: line,
            contextOnly: true,
            listType: marker.listType,
          });
          continue;
        }
        /**
         * 表名行带内容（`识字表<TAB>天<TAB>地`）—— 用户要的**一行多个**录法，
         * 完全走"按表录入"那一套**逐词元判定 + 逐词元记账**。
         *
         * ⚠️ 摊平用 `splitTokens`（Tab **与**空格都算分隔符）：家长从 Excel 里
         *    粘出来的可能是 `识字表<TAB>天 地`（一个格里两个字），
         *    也可能是一个格一个字 —— 两者都要认。
         *
         * ⚠️ 这里**不能**用"表名行 = 第 1 格是表名"来记账：
         *    表名那一格是**标题**，不是数据（`buildStrictTokenRows` 用的
         *    `line.split(/[\t|]/)` 会把表名当词元，所以先把表名摘掉）。
         */
        /**
         * ⚠️ 表名行后面跟着的也可能是**文件格式**（`识字表<TAB>天,tiān<TAB>地,dì`）：
         *    先按 `字,拼音` 词元认一遍，认不出来才走老的逐词元规则。
         */
        const markerReadingTokens = splitReadingTokens(marker.rest.join(' '));
        if (markerReadingTokens !== null) {
          buildReadingTokenRows(marker.listType, markerReadingTokens);
          continue;
        }
        if (buildStrictTokenRows(marker.listType, marker.rest)) continue;
        continue;
      }
    }

    // ---- 「纯汉字串」行（"秋 气 了 子 人 大" / "秋、气、了"）：拆成多个只有字的行 ----
    // 有表头时不启用这个特例：表头已经声明了列的含义，按列走才是对的。
    if (header.length === 0 && isHanziRun(line)) {
      /**
       * 这一行的词元 = `expandHanziRun(line)`（**顿号也算分隔符**）。
       *
       * ⚠️ 不能再用 `buildStrictTokenRows(listType)`（不给 cells）那个默认：
       *    它摊平用的是 `splitTokens`，而 `splitTokens` **不认顿号** ——
       *    于是 `天、地、人`（识字表）会被当成**一个** 5 字符的词元报错，
       *    而 `isHanziRun` 明明已经把它认成"三个单字"了（两处口径打架 ⇒ 假错误）。
       *    实测踩过：本轮加用例时 `天、地、人` 报的正是「词元「天、地、人」是 5 个字符」。
       *
       * ⚠️ **表类型已知时一律走逐词元判定**（三个表都算上）：
       *    词语表里 `天 地 人`（全是单字）以前会**静默变成三行单字**录进词语表 ——
       *    而用户的规则是"词语表里不能录单个的字"。现在逐词元报出来，与
       *    `天地 人` 那条路完全一致（同一套判定、同一套记账）。
       */
      const runChars = expandHanziRun(line);
      if (currentListType !== null && buildStrictTokenRows(currentListType, runChars)) {
        continue;
      }
      for (const ch of runChars) {
        const part: ParsedImportRow = { line: lineNo, raw: line, contextOnly: false, charText: ch };
        if (context) {
          part.lessonTitle = context.lessonTitle;
          part.lessonLabel = context.lessonLabel;
          part.lessonNo = context.lessonNo;
          if (context.unitNo !== undefined) part.unitNo = context.unitNo;
          if (context.textbook !== undefined) part.textbook = context.textbook;
          if (context.edition !== undefined) part.edition = context.edition;
        } else {
          warnings.push({
            line: lineNo,
            raw: line,
            message: `生字「${ch}」没有课文信息（前面没有课文行），会按「无课文」导入`,
          });
        }
        if (part.textbook !== undefined && !textbooks.includes(part.textbook)) {
          textbooks.push(part.textbook);
        }
        if (currentListType !== null) part.listType = currentListType;
        markRecognized();
        stats.charRows += 1;
        rows.push(part);
      }
      continue;
    }

    /**
     * 用一组单元格构造行，并把异常变成逐行错误（单行错误不影响整批）。
     *
     * `tokenCount`：这一行在**账**里算几个词元。默认 1（通用整块导入：一"行"
     * 就是一"条"）；列式行撞上准入规则（`strictReject`）时也按 1 记，
     * 因为那时整行都没收下。
     */
    const safeBuild = (
      cells: RawCells,
      buildOptions: {
        warnNoLesson: boolean;
        singleColumnShaped: boolean;
        /** 这一行没有自己写明表类型时用的表类型（表名上下文 / 默认值）。 */
        listType?: ListType | null;
        tokenCount?: number | undefined;
        /** 表头里有「表」这一列 ⇒ 这一行的结构是**列式**（不按"一串词元"判）。 */
        listTypeColumn?: boolean | undefined;
      },
    ): void => {
      try {
        /**
         * ⚠️ **账**的登记口径：
         *
         *   · `recognized`：`buildRows` 返回了几行就记几行（**在这里**记，
         *     列式行撞上准入规则时由 `strictReject` 把被滤掉的那些撤销）；
         *   · 逐词元那种录法（`天 地 人`）根本不走 `buildRows` ——
         *     它由 `buildStrictTokenRows` **逐个 token** 记账。
         *
         * 两条路互斥（前者 `cells` 有多个格、后者整行只有一格词元），
         * 所以不存在"同一行被记两次"。
         */
        const produced = buildRows({
          cells,
          context,
          line: lineNo,
          raw: line,
          warnNoLesson: buildOptions.warnNoLesson,
          singleColumnShaped: buildOptions.singleColumnShaped,
          listTypeFallback:
            buildOptions.listType !== undefined ? buildOptions.listType : currentListType,
          wordCategoryFallback: defaultWordCategory,
          defaultTextbook: defaultTextbook === '' ? null : defaultTextbook,
          defaultEdition: defaultEdition === '' ? null : defaultEdition,
          errors,
          warnings,
          /**
           * ⚠️ 表头里有「表」列时这一行是**列式**的：准入规则照旧生效，
           *    但**不**按"整行是一串词元"判（见 `buildRows` 里 `multiCell` 的说明）。
           */
          ...(buildOptions.listTypeColumn === true ? { listTypeColumn: true } : {}),
          strictReject: (droppedRows: number) => {
            stats.problem += 1;
            stats.recognized -= droppedRows;
          },
        });
        for (const row of produced) {
          markRow(row);
          markRecognized();
        }
      } catch (err) {
        errors.push({
          line: lineNo,
          raw: line,
          message: err instanceof Error ? err.message : '这一行无法解析',
        });
      }
    };

    /**
     * ---- 「按表录入」的单列词元行 ----
     *
     * 用户要录的那三张表，**一行就是一个格子里的一串词元**（照课本后面的附录粘）。
     * 此时：
     *   · 分隔符是**空格 / Tab 混用都行**（见 `looseTokensOf` 的判据）；
     *   · 每个词元按这一行的表类型逐个判定（字表要单字、词语表要 ≥2 字），
     *     **违规的逐个报错、合法的照常收下**（见 `buildRows` 的准入规则）；
     *   · 词元数与"收下的行数 + 违规的词元数"必须对得上（预览里的账靠它）。
     *
     * ⚠️ `looseTokens` 用 `currentListType`（表名上下文行已经在这之前生效）
     *    而不是 `defaultListType`：`识字表` 那一行之后的字，就是识字表的字。
     */
    const looseTokens = looseTokensOf(line, currentListType);
    const split = splitCells(line, looseTokens);
    const { cells, delimiter: lineDelimiter } = split;

    /**
     * 这一行**是不是表头**（`字,拼音` 这种）—— 与主循环共用 `cells`，
     * 保证"哪些格"的看法完全一致（各自 `splitCells` 一遍会在
     * `天 ，` / `字,拼音` 这种输入上分叉）。
     */
    const looksLikeHeaderRow = header.length === 0 && looksLikeHeader(cells);
    /** 首列形如课序（`第5课 秋天`）—— 那一行是**课文行**，不是词元行。 */
    const looksLikeLessonRow =
      cells.length >= 1 &&
      lineDelimiter === 'spaces' &&
      looksLikeLessonHeader(stripQuotes(normalizeCell(cells[0] ?? '')));

    /**
     * ---- 表头行（`字,拼音`）：**任何位置**都要认 ----
     *
     * ⚠️ 只在"文本最上面"认表头是不够的："按表录入"是粘**一张表**，
     *    形状就是
     *
     *         识字表
     *         字,拼音
     *         长,cháng
     *
     *    —— 表头在第 2 行，而且表名行已经把"录的是哪张表"定下来了。
     *    所以表头行在循环里逐行判（`headerRowIndex` 缺省是 -1）。
     *
     * ⚠️ 它必须排在**表名行识别之前**：`表 | 字 | 版次` 这种表头里
     *    第一格就叫「表」（`toListType('表')` 认识它），先判表名行会把
     *    整行当成"表名 + 内容"，报出一堆莫名其妙的错（实测踩过）。
     */
    {
      if (header.length === 0 && i !== headerRowIndex && looksLikeHeaderRow) {
        const mapped = cells.map((cell) => headerField(cell));
        if (mapped.some((m) => m !== null)) {
          header = mapped;
          headerRowIndex = i;
          headerRaw = cells.map((cell) => normalizeCell(cell));
          for (const [index, name] of headerRaw.entries()) {
            if (mapped[index] === null) {
              warnings.push({
                line: lineNo,
                raw: line,
                message: `表头「${name}」不认识，该列会被忽略`,
              });
            }
          }
        }
        /** 无论认没认出来，表头行都**不产生数据**。 */
        continue;
      }
    }

    /**
     * ---- **文件格式**：`霜,shuāng	吹,chuī	…`（一行 = 课本上的一行）----
     *
     * 见 `buildReadingTokenRows()` 的注释。放在这里（表头之后、词元行之前）：
     *   · 表头行（`字,拼音`）已经在上一步被吃掉，不会进这里；
     *   · 课文行（`第1课 秋天`）由下面的 `looksLikeLessonRow` 判据排除 ——
     *     `splitReadingTokens()` 本身也认不出它（含数字/课），这里只是写明白意图。
     *
     * ⚠️ **只在"录的是哪张表"已知时**生效（`currentListType !== null`）：
     *    通用整块导入里 `字,拼音` 是**两列**，一行多个 `字,拼音` 词元不是它的写法。
     */
    {
      const readingTokens = splitReadingTokens(line);
      if (currentListType !== null && !looksLikeLessonRow && readingTokens !== null) {
        buildReadingTokenRows(currentListType, readingTokens);
        continue;
      }
    }

    /**
     * ---- 「按表录入」的词元行（表名已知）----
     *
     * 这是用户的主录法：照课本后面的附录，一行放**任意多个**词元
     * （空格 / Tab / 换行分隔都行），每个词元必须是"这张表能录的东西"：
     *
     *   · `天 地 人`（识字表）→ 三个字，全收；
     *   · `天 天地`（识字表）→ 「天」收下、「天地」**逐词元报错**；
     *   · `天地 人口`（词语表）→ 两个词，全收；
     *   · `天地 人`（词语表）→ 「天地」收下、「人」报错。
     *
     * ## 判据为什么是"**表名已知**"
     *
     * 而不是"这一行看起来像词元行"：
     *   · 表名已知时，`天 天地` 里的 `天地` 明显不属于识字表 → 报错（要的行为）；
     *   · 表名未知时（通用整块导入），`字` / `词` 是两列，一行里两列都有值是
     *     老文本的正常写法 → **不判**，否则会静默少录数据。
     *
     * ## 三种行要**先**排除，不能当词元判
     *
     *   1. **表头行**（`字,拼音`）—— "按表录入 + 带表头注音"是推荐写法；
     *   2. **课文行**（`第5课 秋天`）—— 那是课题，不是生字；
     *   3. **表名行带内容**（`识字表<TAB>天<TAB>地`）—— 已经在上面处理掉了。
     *
     * ⚠️ 判据里**没有** `looseTokensOf`：只要表名已知、而且这一行不是上面三种，
     *    它就是"这一张表的词元行"，一律走逐词元判定（含**逐词元记账**）——
     *    这样"判了却没记账"不可能发生（账对不上就是 bug）。
     *
     * ⚠️ 传 `cells`（而不是让 `buildStrictTokenRows` 自己按空格切整行）是为了
     *    **列分隔符**：逗号 / 竖线分出来的格要先被尊重
     *    （`天,天地` → 词元 `天` 与 `天地`，而不是一个叫"天,天地"的怪词元）。
     */
    /**
     * ---- 「连着写的汉字」：两张表**相反**的两种处理（2026-10-16 用户实测）----
     *
     * 用户原话（那一段真实粘贴内容）：一行就是课本附录里的一行生字，**汉字之间
     * 一个分隔符都没有**（`天地人你我他`）。于是：
     *
     *   · **识字表 / 写字表** → `autoSplitHanziRun` **自动按单个汉字拆开**，
     *     每个字照常过准入规则（"每个词元恰好一个汉字"不放宽），
     *     并登记"这一行被拆过"（整趟结束后汇总成一条"注意"，**不静默**）；
     *   · **词语表** → `ambiguousWordRun` **不拆也不猜**：连着写的一长串
     *     判断不出词从哪里断开，`checkToken` 逐词元报一条人话错误
     *     （带 token、走同一条记账路径，预览里那张账照旧对得上）。
     *
     * ⚠️ `autoChars` / `ambiguousRun` 只在**整行都是汉字**时才非空 ——
     *    掺了标点 / 字母 / 数字的行一个字都不拆，仍按既有规则逐词元报错。
     */
    const autoChars = autoSplitHanziRun(line, currentListType);
    const ambiguousRun = autoChars === null ? ambiguousWordRun(line, currentListType) : null;

    if (
      currentListType !== null &&
      !looksLikeHeaderRow &&
      !looksLikeLessonRow &&
      ambiguousRun === null &&
      (header.length === 0 || cells.length === 1) &&
      buildStrictTokenRows(currentListType, autoChars ?? looseTokens ?? cells)
    ) {
      if (autoChars !== null) autoSplitLines.push(lineNo);
      continue;
    }

    if (ambiguousRun !== null) {
      checkToken(currentListType, ambiguousRun, lineNo, line, describeAmbiguousWordRun(ambiguousRun));
      continue;
    }

    /**
     * ⚠️ 单个空格的分隔歧义：`第5课 秋天` 是**一行课文**，不是"两列数据"。
     *
     * 判据收紧到"首列形如课序（`looksLikeLessonHeader`）"：`秋天 天气` 这种
     * 首列没有课序的行仍然按"词表"处理，不会被误当成课题而**静默丢掉数据**。
     */
    if (
      header.length === 0 &&
      lineDelimiter === 'spaces' &&
      cells.length >= 2 &&
      looksLikeLessonHeader(stripQuotes(normalizeCell(cells[0] ?? '')))
    ) {
      const lessonCell = stripQuotes(normalizeCell(cells[0] ?? ''));
      const rest = cells
        .slice(1)
        .map((c) => stripQuotes(normalizeCell(c)))
        .filter((c) => c !== '');

      const parsedHead = parseLessonCell(lessonCell);
      if (parsedHead.title !== '') {
        context = {
          lessonTitle: parsedHead.title,
          lessonLabel: parsedHead.label,
          lessonNo: parsedHead.no,
        };
      }

      if (rest.length > 0) {
        if (rest.length > 1) {
          warnings.push({
            line: lineNo,
            raw: line,
            message: `一行里出现了多个内容（「${rest.join('」「')}」），只取第一个「${rest[0]}」；其余请换行写`,
          });
        }
        const first = rest[0]!;
        const single: Partial<Record<FieldKey, string>> =
          [...first].length === 1 ? { char: first } : { word: first };
        safeBuild({ ...emptyCells(), lesson: lessonCell, ...single }, {
          warnNoLesson: false,
          singleColumnShaped: false,
        });
      }
      continue;
    }

    // ---- 0) 空格/顿号分隔、且没有表头：两种可能，先按内容判断 ----
    //
    //   a) 单词表行：`秋天 天气 叶子` / `秋天、天气、叶子` / `小小的船`
    //      —— 每个单元格是一个词（顿号在中文里就是并列词语的分隔符）。
    //   b) 课文行：`第1课 秋天`（单个空格）—— 这是课题，不是词表。
    //
    // 判据：单个空格分隔时，**全部单元格都是汉字串**才按词表处理；
    //       出现"第1课"这种含数字的单元格就按课文行处理（交给下面的 2a）。
    //       顿号则一律按词表处理（顿号不会出现在课文行的课题里）。
    if (header.length === 0 && (lineDelimiter === 'ideographic' || lineDelimiter === 'spaces')) {
      const tokens = cells.map((c) => stripQuotes(normalizeCell(c))).filter((c) => c !== '');
      const allHanzi = tokens.length > 0 && tokens.every((t) => [...t].every((ch) => isHanzi(ch)));
      /**
       * 判据（三种情况）：
       *   · 顿号分隔 → 用顿号摊开的**并列词语**（顿号不会出现在课文行的课题里）；
       *   · 多个空格 → 同上（列式粘贴请用制表符，见文档）；
       *   · 单个空格 → 只有"每个单元格都是汉字串"才按词表处理。
       *     `第1课 秋天` 的首个单元格含数字，所以走课文行分支（2a）；
       *     `秋天 天气 叶子` 每个单元格都是汉字串，按词表处理。
       */
      const isWordList =
        tokens.length > 1
          ? lineDelimiter === 'ideographic' || allHanzi
          : lineDelimiter === 'ideographic' ||
            (tokens.length === 1 && !looksLikeLessonHeader(tokens[0]!));
      if (isWordList) {
        /**
         * ⚠️ 走到这里说明**"录的是哪张表"还不知道**（`currentListType === null`）——
         *    因为"表名已知"的那条路已经在上面被 `buildStrictTokenRows` 接走了。
         *    所以这里保持改造前的口径：每个格是一个词，单格的一个字仍然算"字"
         *    （`singleColumnShaped`）。
         */
        const tokenTexts = tokens;
        for (let k = 0; k < tokenTexts.length; k++) {
          const tokenText = tokenTexts[k]!;
          safeBuild({ ...emptyCells(), word: tokenText }, {
            // 词表行前面没有课文行时，只在第一行提示一次，避免刷屏
            warnNoLesson: k === 0,
            singleColumnShaped: true,
          });
        }
        continue;
      }    }

    const values = header.length > 0 ? mapWithHeader(cells, header) : mapByPosition(cells);

    /**
     * ⚠️ **表头里有「表」这一列**（`表 | 字 | 版次`）时，这一行的结构是**列式**的：
     *    "录的是哪张表"由那一列一格一格地写着，所以**不再**按"整行是一串词元"判。
     *    这一条不加的话，`识字表 | 天地 | 2024年8月第1版` 会被当成"三个词元"，
     *    连版次那一格都要报"识字表里不能录词语"（假错误）。
     *
     * ⚠️ 但**准入规则仍然生效**（只是换成按列判）：`字` 列写词照样报错、不落库。
     */
    const hasListTypeColumn = header.includes('listType');

    /**
     * 单空格 + 无表头时还有第三种形状：**课序与课题用空格连写**
     * （`第5课 秋天`、`5 秋天`、`5 秋天 天气`）。
     *
     * 判据收紧到"首列含数字且形如课序"：只有这样的行才把首列当课文，
     * 其余单元格继续按默认列序走（忽略）。
     *
     * ⚠️ 刻意**不**放宽到"首列是汉字也算课文"：`小小的船 月儿` 首列没有数字，
     *    它是"一篇课文名 + 一个词"还是"两个词"，在文本里根本无从判断。
     *    猜错的代价是**静默丢数据**，所以宁可把它当词表（两行词），
     *    并在文档里明确要求：列式粘贴请用制表符。
     */
    if (
      header.length === 0 &&
      lineDelimiter === 'spaces' &&
      cells.length >= 2 &&
      /\d/.test(stripQuotes(normalizeCell(cells[0] ?? ''))) &&
      looksLikeLessonHeader(stripQuotes(normalizeCell(cells[0] ?? '')))
    ) {
      values.lesson = stripQuotes(normalizeCell(cells[0] ?? ''));
      delete values.char;
    }

    const picked = pickCells(values);
    const hasChar = picked.char !== '';
    const hasWord = picked.word !== '';

    // ---- 2a) 课文上下文行（只有课文，别的列都是空的）----
    // "只有课文"的判据要严格：如果这一行还填了类别/年级/课本，那它本意是一个
    // **数据行**（只是忘了填字或词），报"既没有字也没有词"比默默当成课文行有用。
    const onlyLessonFilled =
      !hasChar &&
      !hasWord &&
      picked.lesson !== '' &&
      picked.category === '' &&
      picked.grade === '' &&
      picked.semester === '' &&
      picked.fromTextbook === '';
    /**
     * ⚠️ 判据里**没有** `picked.textbook` / `picked.edition`。
     *
     * 它们和课文一样是**上下文**（"下面的行都属于这本书这一版"），不是数据；
     * 写在一起是家长很自然的写法：
     *
     *     课本	版次	课文
     *     人民教育出版社	2024年8月第1版	第1课 秋天
     *     ...
     *
     * 把它们算成"填了内容"的话，这样一行会被判成"漏了字/词"的数据行并报错 ——
     * 而那正是**推荐**的录法（课本与课文只写一次；版次已经不用了）。
     *
     * ⚠️ 但 `年级` / `学期` 仍然算"填了内容"：它们和字词是**同一行**的归属，
     *    一行写了年级学期却没写字词，多半是漏填 —— 报出来比默默改上下文有用。
     *    那两条列也**不在**这句注释里被"顺手放宽"。
     */
    if (onlyLessonFilled) {
      const parsed = parseLessonCell(picked.lesson);
      if (parsed.title === '') {
        errors.push({ line: lineNo, raw: line, message: '课文行的课题是空的' });
        continue;
      }
      const noFinal = picked.no !== '' ? parseLessonNo(picked.no).no : parsed.no;
      const unitNo = picked.unit !== '' ? Number.parseInt(picked.unit, 10) || undefined : undefined;
      context = {
        lessonTitle: parsed.title,
        lessonLabel: parsed.label,
        lessonNo: noFinal,
        ...(unitNo !== undefined ? { unitNo } : {}),
        ...(picked.textbook !== '' ? { textbook: picked.textbook } : {}),
        /**
         * ⚠️ 课文行也带**版次**：家长写 `课本	版次	年级	学期` 表头 + 一行课题，
         *    下面的字词行没写版次时继承它 —— 否则"课文行 + 生字串"这种最省事的
         *    录法会因为"缺版次"整段报错。
         */
        ...(picked.edition !== '' ? { edition: picked.edition } : {}),
      };
      stats.contextLines += 1;
      warnings.push({
        line: lineNo,
        raw: line,
        message: '这一行只有课文，没有任何「字」或「词」——按课文行处理（设定后续行的课文）',
      });
      rows.push({
        line: lineNo,
        raw: line,
        contextOnly: true,
        lessonTitle: parsed.title,
        lessonLabel: parsed.label,
        lessonNo: noFinal,
        ...(unitNo !== undefined ? { unitNo } : {}),
        ...(picked.textbook !== '' ? { textbook: picked.textbook } : {}),
        ...(picked.edition !== '' ? { edition: picked.edition } : {}),
      });
      continue;
    }

    // ---- 2b) 既没有字也没有词 ----
    if (!hasChar && !hasWord) {
      errors.push({
        line: lineNo,
        raw: line,
        message: '这一行既没有「字」也没有「词」（若想设定课文，请单独写一行课题）',
      });
      continue;
    }

    // ---- 2c) 构造并校验 ----
    safeBuild(picked, {
      warnNoLesson: true,
      singleColumnShaped: false,
      ...(hasListTypeColumn ? { listTypeColumn: true } : {}),
    });
  }

  // ---- 2d) 课本 / 版次：**补默认值**（2026-10-18 起版次选填、不再有"缺版次"）----
  //
  // 为什么放在整趟循环之后、而不是每一行里：有几条分支（"纯汉字串"行、
  // 表名行展开出的字/词行）根本不走 `buildRows` —— 它们也得能继承调用方的默认值。
  {
    /**
     * 先把调用方给的默认值补上（"按书 + 按表录入"时界面已经选好了书与版次，
     * 粘贴框里只有字/词）。
     *
     * ⚠️ 版次是**选填**的，所以这里不再有"缺版次就报错"那一段：
     *    课本身份是「出版社 + 年级 + 学期」，版次空着完全合法。
     * ⚠️ 年级 / 学期不在这里补：它们由 `ParsedImportRow` 逐行带着（来自文本的
     *    「年级」「学期」列 / 课文行继承），缺失时由服务层按"请求默认值 →
     *    这本书自己的册别"的顺序兜；兜不住再报 400（见 `importDictation`）。
     */
    if (defaultTextbook !== '' || defaultEdition !== '') {
      for (const row of rows) {
        if (row.edition === undefined && defaultEdition !== '') row.edition = defaultEdition;
        if (row.textbook === undefined && defaultTextbook !== '') row.textbook = defaultTextbook;
      }
    }
  }

  /**
   * 课本清单按**最终留下的行**重算。
   *
   * ⚠️ 不能在解析过程中顺手 push：报告里列出一个**不会导入**的课本会让家长
   *    以为它建出来了。
   */
  textbooks.length = 0;
  for (const row of rows) {
    if (row.contextOnly) continue;
    if (row.textbook !== undefined && row.textbook !== '' && !textbooks.includes(row.textbook)) {
      textbooks.push(row.textbook);
    }
  }

  // ---- 3) 汇总 ----
  const dataRowCount = rows.filter((r) => !r.contextOnly).length;

  /**
   * ---- 词元总数 = 收下的 + 没收下的 ----
   *
   * ⚠️ 这里**不是**"再数一遍"，而是把两条路径上**直接登记**的两个数加起来：
   *    · `recognized`：`markRecognized()`（每收下一个词元时调用）；
   *    · `problem`：`checkToken()` 判定违规时 +1。
   *    于是恒等式 `tokens = recognized + problem` 是**结构上成立**的 ——
   *    预览里给家长核对的那张账不会因为"两处口径不一致"而对不上。
   */
  stats.tokens = stats.recognized + stats.problem;

  /**
   * "**替你拆了字**"必须说出来（用户要求"不要静默"）。
   *
   * 汇总成**一条**（一张识字表几十行连着写就会刷屏），行号列出来让人能对上原文。
   * 放在 3) 汇总这里而不是循环里：那时才拿得到全部行号。
   */
  if (autoSplitLines.length > 0) {
    warnings.push({
      line: 0,
      raw: '',
      message: describeAutoSplitHanziRun(autoSplitLines),
    });
  }

  if (dataRowCount === 0 && errors.length === 0) {
    warnings.push({
      line: 0,
      raw: '',
      message: '没有解析出任何数据行：请检查是否只贴了说明文字，或列的分隔符不是制表符/逗号',
    });
  }

  return {
    rows,
    errors,
    warnings,
    header: headerRaw,
    delimiter,
    textbooks,
    dataRowCount,
    stats,
  };
}

// ---------------------------------------------------------------- 表名行展开用的两个小工具

/**
 * 构造一个"只有字"的行（表名行展开时用）。
 *
 * 与 `buildRows` 的字分支**同一套校验**（单字 + 汉字），只是这里不需要
 * "整行共享字段"那一圈判断（表名行里没有那些列）。
 */
function parseDictationImportChar(
  charText: string,
  line: number,
  raw: string,
  context: LessonContext | null,
  listType: ListType | null = null,
): ParsedImportRow {
  const row: ParsedImportRow = { line, raw, contextOnly: false, charText };
  if (context !== null) {
    row.lessonTitle = context.lessonTitle;
    row.lessonLabel = context.lessonLabel;
    row.lessonNo = context.lessonNo;
    if (context.unitNo !== undefined) row.unitNo = context.unitNo;
    if (context.textbook !== undefined) row.textbook = context.textbook;
    if (context.edition !== undefined) row.edition = context.edition;
  }
  if (listType !== null) row.listType = listType;
  return row;
}

/**
 * 构造一个"只有词"的行（表名行 / 单元格里的词）。非法时返回 `null`
 * （长度或非汉字），由调用方决定报错还是忽略。
 */
function parseDictationImportWord(
  wordText: string,
  line: number,
  raw: string,
  context: LessonContext | null,
  category: string | null,
): ParsedImportRow | null {
  const chars = [...wordText];
  if (chars.length < 1 || chars.length > 16) return null;
  if (!chars.every((c) => isHanzi(c))) return null;
  const row: ParsedImportRow = { line, raw, contextOnly: false, wordText };
  if (context !== null) {
    row.lessonTitle = context.lessonTitle;
    row.lessonLabel = context.lessonLabel;
    row.lessonNo = context.lessonNo;
    if (context.unitNo !== undefined) row.unitNo = context.unitNo;
    if (context.textbook !== undefined) row.textbook = context.textbook;
    if (context.edition !== undefined) row.edition = context.edition;
  }
  if (category !== null) row.category = category;
  return row;
}

// ---------------------------------------------------------------- 类别映射

/**
 * 汉字类别：1类字 / 2类字 —— **改造前的旧口径**（迁移 0009 已删掉
 * `hanzi.category` 列）。
 *
 * ⚠️ 保留它只有一个理由：老粘贴文本里的 `类别` 列写的就是这两个词，
 *    而它同时**携带着"这个字在哪张表上"的信息**（1类字 = 会写 = 写字表）。
 *    导入时由 `toListType()` 把它翻译成 `shizi` / `xiezi`（见 `buildRows`），
 *    所以新代码**不要**再用这两个值当"字的分类"，用 `ListType`。
 */
export const HANZI_CATEGORIES = ['type1', 'type2'] as const;
export type HanziCategory = (typeof HANZI_CATEGORIES)[number];

/** 词语类别：常用词 / 词语 / 成语。 */
export const WORD_CATEGORIES = ['common', 'phrase', 'idiom'] as const;
export type WordCategory = (typeof WORD_CATEGORIES)[number];

/**
 * 类别文本 → **汉字**类别（认不出返回 null）。**只用于"这一行是字还是词"的消歧**
 * 与老文本兼容；"在哪张表上"请用 `toListType()`。
 *
 * 只认"1类字 / 2类字 / 会写 / 会认"这一组。刻意**不**把 `1` / `2` 当类别 ——
 * 它们太容易被误当课序或序号，让"这一行到底是字还是词"变得不可预测。
 */
export function toHanziCategory(raw: string | undefined): HanziCategory | null {
  if (raw === undefined) return null;
  const text = normalizeCell(raw).replace(/\s/g, '');
  if (text === '1类字' || text === '一类字' || text === '会写字' || text === 'type1') return 'type1';
  if (text === '2类字' || text === '二类字' || text === '会认字' || text === 'type2') return 'type2';
  return null;
}

/**
 * 类别文本 → **词语**类别（认不出返回 null）。
 *
 * 顺序有讲究：先认特定的（成语 → 常用词 → 词语），最后才是泛化的 `词`。
 * 反过来写的话「常用词」会被`text.includes('词')`先截走，归成 phrase。
 */
export function toWordCategory(raw: string | undefined): WordCategory | null {
  if (raw === undefined) return null;
  const text = normalizeCell(raw).replace(/\s/g, '');
  if (text.includes('成语')) return 'idiom';
  if (text.includes('常用')) return 'common';
  if (text.includes('词')) return 'phrase';
  if (text === 'common') return 'common';
  if (text === 'phrase') return 'phrase';
  if (text === 'idiom') return 'idiom';
  return null;
}

/** 判断一个类别文本是不是汉字类别（用于"这一行是字还是词"的消歧）。 */
export function categoryKind(raw: string | undefined): 'hanzi' | 'word' | null {
  if (raw === undefined) return null;
  /** `识字表` / `写字表` 也是"这是字"的信号（新口径）；旧口径见 `toHanziCategory`。 */
  if (toListType(raw) === 'shizi' || toListType(raw) === 'xiezi') return 'hanzi';
  if (toHanziCategory(raw) !== null) return 'hanzi';
  if (toWordCategory(raw) !== null) return 'word';
  return null;
}
